import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight, ClipboardCheck, Ruler, PackagePlus, Images } from 'lucide-react';
import PhotoCapture from '../PhotoCapture';
import { PhotoData } from '../../services/CameraService';
import { RetrofitSurveyData, getRetrofitSurveyAddress } from '../../utils/retrofitSurveyStorage';
import { useRetrofitPhotoUpload } from '../../hooks/useRetrofitPhotoUpload';
import { useSectionAutosave } from '../../hooks/useSectionAutosave';
import PropertyElevationDiagram from '../PropertyElevationDiagram';
import RoomInRoofDiagram from '../RoomInRoofDiagram';
import PhotoGalleryModal from '../PhotoGalleryModal';
import { RoofPitchAngleMeasurer } from '../RoofPitchAngleMeasurer';
import { analyzeSectionFileSize, logFileSizeAnalysis } from '../../utils/fileSizeAnalysis';
import { deletePhotoFromSupabase } from '../../utils/supabaseStorage';

interface DisplayablePhotoData extends PhotoData {
  displayId: string;
  dataUrl: string;
  fileName?: string;
  url?: string;
  uploading?: boolean;
  error?: string;
}

interface MeasureSpecificationFormData {
  suitableMeasures: string[];
  wallInsulationType: string;
  wallInsulationElevationsCount: string;

  // Loft Survey
  mainPropertyRoofType: string;
  loftNeedsInsulating: boolean | null;
  loftHasExistingInsulation: boolean | null;
  mainLoftDepthOfInsulation: string;
  accessToAllAreasOfLoft: boolean | null;
  loftHatchAlreadyPresent: boolean | null;
  isThereALoftLadder: boolean | null;
  isTheLoftHatchAlreadyInsulated: boolean | null;
  isTheLoftHatchDraughtProofed: boolean | null;
  loftHatchLengthMM: string;
  loftHatchWidthMM: string;
  mainPropertyLoftLength: string;
  mainPropertyLoftWidth: string;
  mainPropertyLoftHeight: string;
  mainPropertyRoofPitch: string;
  inaccessibleAreaM2: string;
  walkboardsRequired: boolean | null;
  tenantsBelongingsToBeRemoved: boolean | null;
  explainedToOccupier: boolean | null;
  loftDownlightsPresent: boolean | null;
  downlightCoversRequired: boolean | null;
  coldWaterTankPresent: boolean | null;
  pipeworkPresent: boolean | null;
  coldWaterTankPipeworkNeedInsulation: boolean | null;
  roofVentsPresent: boolean | null;
  verticalFlueInLoft: boolean | null;
  highCurrentCablesPresent: boolean | null;
  numberOfExtensions: number;
  extensions: ExtensionLoftData[];

  // Heating Controls (now in HeatingSystemForm)
  fullSetFunctioningControls: boolean | null;
  roomThermostat: boolean | null;
  boilerProgrammer: boolean | null;
  smartThermostat: boolean | null;
  systemHasBypassRadiator: boolean | null;
  
  // Heating System
  isBoilerPresent: boolean | null;
  boilerManufacturerName: string;
  boilerBrand: string;
  modelQualifier: string;
  boilerType: string;
  fuelType: string;
  sapWinterEfficiency: string;
  ageOfBoiler: string;
  yearOfCommissioning: string;
  serialNumber: string;
  boilerLocation: string;
  boilerPosition: string;
  anyOtherBoilers: boolean | null;
  otherBoilersDetails: string;
  isCondensatePipe: boolean | null;
  condensatePipeTermination: string;
  newSystemHeat100Percent: boolean | null;
  postHeatingSystem: string;
  flueType: string;
  flueGuardRequired: boolean | null;
  flueFittingKitRequired: boolean | null;
  plumeKitRequired: boolean | null;
  frostProtectionRequired: boolean | null;
  condenseTermination: string;
  gasMeterBoilerDistance: string;
  remedialWorksToBrickwork: boolean | null;
  newBoilerLocation: string;
  weatherCompensatorNeeded: boolean | null;
  loadCompensatorNeeded: boolean | null;
  trvsRequired: boolean | null;
  lockShieldsRequired: boolean | null;
  coldWaterPressureAdequate: boolean | null;
  mainsBoardLocation: string;
  electricityMeterLocation: string;
  stopTapLocation: string;
  propertyEarthBondingPresent: boolean | null;
  fuseSpurPresent: boolean | null;
  pipeworkInUnheatedAreas: boolean | null;

  // RIR Survey
  buildingType: string;
  rirRoomsCount: string;
  rirFloorArea: string;
  rirResidualLoftArea: string;
  roofPitchRIR: string;
  internalSurfacesDamp: boolean | null;
  needsInsulating: boolean | null;
  downlightsPresent: boolean | null;
  rirFreeOfStoredItems: boolean | null;
  plumberRequired: boolean | null;
  electricianRequired: boolean | null;
  skirtingBoardsRequired: boolean | null;
  skirtingBoardLength: string;
  walkwayRequired: boolean | null;
  workingTanksPipesPresent: boolean | null;
  fixedStairCase: boolean | null;
  dormerPresent: boolean | null;
  
  // Stud walls
  studWallsPresent: boolean | null;
  studWallConstruction: string;
  studWallTotalArea: string;
  studWallElementInsulated: boolean | null;
  studWallRequiresInsulation: boolean | null;
  studWallInstallationMethod: string;
  studWallInsulationType: string;
  studWall100PercentInstalled: boolean | null;
  studWallInsulationThickness: string;
  
  // Sloping Ceilings
  slopingCeilingsPresent: boolean | null;
  slopingCeilingArea: string;
  slopingCeilingElementInsulated: boolean | null;
  slopingCeilingRequiresInsulation: boolean | null;
  slopingCeilingInstallationMethod: string;
  slopingCeilingInsulationType: string;
  slopingCeiling100PercentInstalled: boolean | null;
  slopingCeilingInsulationThickness: string;
  
  // Gable Walls
  gableWallsPresent: boolean | null;
  gableWallConstruction: string;
  gableWallTotalArea: string;
  gableWallElementInsulated: boolean | null;
  gableRequiresInsulation: boolean | null;
  gableWallInstallationMethod: string;
  gableWallInsulationType: string;
  gableWall100PercentInstalled: boolean | null;
  gableWallInsulationThickness: string;

  // Party Walls
  partyWallsPresent: boolean | null;
  partyWallConstruction: string;
  partyWallTotalArea: string;
  partyWallElementInsulated: boolean | null;
  partyWallRequiresInsulation: boolean | null;
  partyWallInstallationMethod: string;
  partyWallInsulationType: string;
  partyWall100PercentInstalled: boolean | null;
  partyWallInsulationThickness: string;

  // Dormer Windows
  dormerWindowsPresent: boolean | null;
  dormerWindowCount: string;
  dormerCeilingTotalArea: string;
  dormerRevealsTotalArea: string;
  dormerElementInsulated: boolean | null;
  dormerWindowsRequiresInsulation: boolean | null;
  dormerWindowInstallationMethod: string;
  dormerWindowInsulationType: string;
  dormerWindow100PercentInstalled: boolean | null;
  dormerWindowInsulationThickness: string;

  // Residual Areas
  residualAreasPresent: boolean | null;
  residualLoftSpace: string;
  existingInsulationPresent: 'na' | 'yes' | 'no' | null;
  existingInsulationThickness: string;
  residualAreasToBeInsulated: string;
  
  // Flat Ceilings
  flatCeilingPresent: 'na' | 'yes' | 'no' | null;
  totalAreaFlatCeiling: string;
  flatCeilingElementInsulated: boolean | null;
  flatCeilingRequiresInsulation: boolean | null;
  accessAboveFlatCeiling: boolean | null;
  flatCeilingInstallationMethod: string;
  flatCeilingInsulationType: string;
  flatCeiling100PercentInstalled: boolean | null;
}

interface ExtensionLoftData {
  id: number;
  propertyRoofType: string;
  loftNeedsInsulating: boolean | null;
  loftHasExistingInsulation: boolean | null;
  depthOfInsulation: string;
  accessToAllAreas: boolean | null;
  loftHatchPresent: boolean | null;
  loftLadderPresent: boolean | null;
  loftHatchInsulated: boolean | null;
  loftHatchDraughtProofed: boolean | null;
  loftHatchLengthMM: string;
  loftHatchWidthMM: string;
  loftLength: string;
  loftWidth: string;
  loftHeight: string;
  roofPitch: string;
  inaccessibleAreaM2: string;
  walkboardsRequired: boolean | null;
  tenantsBelongingsToBeRemoved: boolean | null;
  explainedToTenants: boolean | null;
  downlightsPresent: boolean | null;
  downlightCoversRequired: boolean | null;
  coldWaterTankPresent: boolean | null;
  pipeworkPresent: boolean | null;
  coldWaterTankPipeworkNeedInsulation: boolean | null;
  roofVentsPresent: boolean | null;
  verticalFlueInLoft: boolean | null;
  highCurrentCablesPresent: boolean | null;
}

interface ExtensionLoftPhotos {
  id: number;
  insulationDepthEvidence: DisplayablePhotoData | null;
  loftHatchEvidence: DisplayablePhotoData | null;
  roofPitchEvidence: DisplayablePhotoData | null;
  loftAreaImages: DisplayablePhotoData | null;
  downlightsImage: DisplayablePhotoData | null;
  coldWaterTankPipeworkEvidence: DisplayablePhotoData | null;
  imagesOfRoofVents: DisplayablePhotoData | null;
}

interface MeasureSpecificationPhotos {
  currentBoilerImage: DisplayablePhotoData | null;
  boilerDataBadgeImage: DisplayablePhotoData | null;
  fluePipeImage: DisplayablePhotoData | null;
  
  // Heating Controls Photos
  preExistingHeatingControlsImages: DisplayablePhotoData | null;
  bypassRadiatorImage: DisplayablePhotoData | null;

  // Loft Survey Photos
  mainPropertyInsulationDepthEvidence: DisplayablePhotoData[];
  mainLoftHatchEvidence: DisplayablePhotoData[];
  mainPropertyRoofPitchEvidence: DisplayablePhotoData[];
  mainLoftAreaImages: DisplayablePhotoData[];
  apexMeasurementEvidence: DisplayablePhotoData[];
  partyWallEvidence: DisplayablePhotoData[];
  downlightImage: DisplayablePhotoData[];
  coldWaterTankPipeworkEvidence: DisplayablePhotoData[];
  roofVentsImage: DisplayablePhotoData[];
  extensionPhotos: ExtensionLoftPhotos[];
  
  // Heating System Photos
  imageOfCurrentBoiler: DisplayablePhotoData | null;
  boilerDataBadge: DisplayablePhotoData | null;
  imageOfFluePipe: DisplayablePhotoData | null;
  imageOfCondensatePipe: DisplayablePhotoData | null;
  stopTapImages: DisplayablePhotoData | null;

  // RIR Photos
  roofPitchEvidenceRIR: DisplayablePhotoData | null;
  downlightsEvidence: DisplayablePhotoData | null;
  roofInRoofCorner1: DisplayablePhotoData | null;
  roofInRoofCorner2: DisplayablePhotoData | null;
  rirExteriorElevations: DisplayablePhotoData[];
  rirInternalRoomPhotos: DisplayablePhotoData[];
  rirFlatCeilingInsulation: DisplayablePhotoData[];
  rirSlopingCeilingInsulation: DisplayablePhotoData[];
  rirStudWallsInsulation: DisplayablePhotoData[];
  rirDormerPhotos: DisplayablePhotoData[];
  roofInRoofCorner3: DisplayablePhotoData | null;
  roofInRoofCorner4: DisplayablePhotoData | null;
  studWallImages: DisplayablePhotoData | null;
  slopingCeilingImages: DisplayablePhotoData | null;
  gableWallImages: DisplayablePhotoData | null;
  partyWallImages: DisplayablePhotoData | null;
  dormerWindowsImages: DisplayablePhotoData | null;
  residualAreaImages: DisplayablePhotoData | null;
  flatCeilingImage: DisplayablePhotoData | null;
}

interface MeasureSpecificationFormProps {
  onNavigateNext: () => void;
  onNavigateBack: () => void;
  floorPlanId?: string;
  surveyData?: RetrofitSurveyData;
  surveyId?: string;
  onDataUpdate?: (updater: (prev: RetrofitSurveyData) => RetrofitSurveyData, sectionId?: string) => void;
  onSaveImmediately?: () => Promise<string>;
}

const initialFormData: MeasureSpecificationFormData = {
  suitableMeasures: [],
  wallInsulationType: '',
  wallInsulationElevationsCount: '',
  
  // Loft Survey
  mainPropertyRoofType: '',
  loftNeedsInsulating: null,
  loftHasExistingInsulation: null,
  mainLoftDepthOfInsulation: '',
  accessToAllAreasOfLoft: null,
  loftHatchAlreadyPresent: null,
  isThereALoftLadder: null,
  isTheLoftHatchAlreadyInsulated: null,
  isTheLoftHatchDraughtProofed: null,
  loftHatchLengthMM: '',
  loftHatchWidthMM: '',
  mainPropertyLoftLength: '',
  mainPropertyLoftWidth: '',
  mainPropertyLoftHeight: '',
  mainPropertyRoofPitch: '',
  inaccessibleAreaM2: '0.000',
  walkboardsRequired: null,
  tenantsBelongingsToBeRemoved: null,
  explainedToOccupier: null,
  loftDownlightsPresent: null,
  downlightCoversRequired: null,
  coldWaterTankPresent: null,
  pipeworkPresent: null,
  coldWaterTankPipeworkNeedInsulation: null,
  roofVentsPresent: null,
  verticalFlueInLoft: null,
  highCurrentCablesPresent: null,
  numberOfExtensions: 0,
  extensions: [],

  // Heating Controls (now in HeatingSystemForm)
  fullSetFunctioningControls: null,
  roomThermostat: null,
  boilerProgrammer: null,
  smartThermostat: null,
  systemHasBypassRadiator: null,

  // Heating System
  isBoilerPresent: null,
  boilerManufacturerName: '',
  boilerBrand: '',
  modelQualifier: '',
  boilerType: '',
  fuelType: '',
  sapWinterEfficiency: '',
  ageOfBoiler: '',
  yearOfCommissioning: '',
  serialNumber: '',
  boilerLocation: '',
  boilerPosition: '',
  anyOtherBoilers: null,
  otherBoilersDetails: '',
  isCondensatePipe: null,
  condensatePipeTermination: '',
  newSystemHeat100Percent: null,
  postHeatingSystem: '',
  flueType: '',
  flueGuardRequired: null,
  flueFittingKitRequired: null,
  plumeKitRequired: null,
  frostProtectionRequired: null,
  condenseTermination: '',
  gasMeterBoilerDistance: '',
  remedialWorksToBrickwork: null,
  newBoilerLocation: '',
  weatherCompensatorNeeded: null,
  loadCompensatorNeeded: null,
  trvsRequired: null,
  lockShieldsRequired: null,
  coldWaterPressureAdequate: null,
  mainsBoardLocation: '',
  electricityMeterLocation: '',
  stopTapLocation: '',
  propertyEarthBondingPresent: null,
  fuseSpurPresent: null,
  pipeworkInUnheatedAreas: null,

  // RIR Survey
  buildingType: '',
  rirRoomsCount: '',
  rirFloorArea: '',
  rirResidualLoftArea: '',
  roofPitchRIR: '',
  internalSurfacesDamp: null,
  needsInsulating: null,
  downlightsPresent: null,
  rirFreeOfStoredItems: null,
  plumberRequired: null,
  electricianRequired: null,
  skirtingBoardsRequired: null,
  skirtingBoardLength: '',
  walkwayRequired: null,
  workingTanksPipesPresent: null,
  fixedStairCase: null,
  dormerPresent: null,
  
  // Stud walls
  studWallsPresent: null,
  studWallConstruction: '',
  studWallTotalArea: '',
  studWallElementInsulated: null,
  studWallRequiresInsulation: null,
  studWallInstallationMethod: '',
  studWallInsulationType: '',
  studWall100PercentInstalled: null,
  studWallInsulationThickness: '',
  
  // Sloping Ceilings
  slopingCeilingsPresent: null,
  slopingCeilingArea: '',
  slopingCeilingElementInsulated: null,
  slopingCeilingRequiresInsulation: null,
  slopingCeilingInstallationMethod: '',
  slopingCeilingInsulationType: '',
  slopingCeiling100PercentInstalled: null,
  slopingCeilingInsulationThickness: '',
  
  // Gable Walls
  gableWallsPresent: null,
  gableWallConstruction: '',
  gableWallTotalArea: '',
  gableWallElementInsulated: null,
  gableRequiresInsulation: null,
  gableWallInstallationMethod: '',
  gableWallInsulationType: '',
  gableWall100PercentInstalled: null,
  gableWallInsulationThickness: '',

  // Party Walls
  partyWallsPresent: null,
  partyWallConstruction: '',
  partyWallTotalArea: '',
  partyWallElementInsulated: null,
  partyWallRequiresInsulation: null,
  partyWallInstallationMethod: '',
  partyWallInsulationType: '',
  partyWall100PercentInstalled: null,
  partyWallInsulationThickness: '',

  // Dormer Windows
  dormerWindowsPresent: null,
  dormerWindowCount: '',
  dormerCeilingTotalArea: '',
  dormerRevealsTotalArea: '',
  dormerElementInsulated: null,
  dormerWindowsRequiresInsulation: null,
  dormerWindowInstallationMethod: '',
  dormerWindowInsulationType: '',
  dormerWindow100PercentInstalled: null,
  dormerWindowInsulationThickness: '',

  // Residual Areas
  residualAreasPresent: null,
  residualLoftSpace: '',
  existingInsulationPresent: null,
  existingInsulationThickness: '',
  residualAreasToBeInsulated: '',
  
  // Flat Ceilings
  flatCeilingPresent: null,
  totalAreaFlatCeiling: '',
  flatCeilingElementInsulated: null,
  flatCeilingRequiresInsulation: null,
  accessAboveFlatCeiling: null,
  flatCeilingInstallationMethod: '',
  flatCeilingInsulationType: '',
  flatCeiling100PercentInstalled: null,
};

const initialPhotos: MeasureSpecificationPhotos = {
  currentBoilerImage: null,
  boilerDataBadgeImage: null,
  fluePipeImage: null,
  
  // Heating Controls Photos
  preExistingHeatingControlsImages: null,
  bypassRadiatorImage: null,

  // Loft Survey Photos
  mainPropertyInsulationDepthEvidence: [],
  mainLoftHatchEvidence: [],
  mainPropertyRoofPitchEvidence: [],
  mainLoftAreaImages: [],
  apexMeasurementEvidence: [],
  partyWallEvidence: [],
  downlightImage: [],
  coldWaterTankPipeworkEvidence: [],
  roofVentsImage: [],
  extensionPhotos: [],

  // Heating System Photos
  imageOfCurrentBoiler: null,
  boilerDataBadge: null,
  imageOfFluePipe: null,
  imageOfCondensatePipe: null,
  stopTapImages: null,

  // RIR Photos
  roofPitchEvidenceRIR: null,
  downlightsEvidence: null,
  roofInRoofCorner1: null,
  roofInRoofCorner2: null,
  rirExteriorElevations: [],
  rirInternalRoomPhotos: [],
  rirFlatCeilingInsulation: [],
  rirSlopingCeilingInsulation: [],
  rirStudWallsInsulation: [],
  rirDormerPhotos: [],
  roofInRoofCorner3: null,
  roofInRoofCorner4: null,
  studWallImages: null,
  slopingCeilingImages: null,
  gableWallImages: null,
  partyWallImages: null,
  dormerWindowsImages: null,
  residualAreaImages: null,
  flatCeilingImage: null,
};

// Common Styles
const fieldsetClassName = "border border-gray-300 p-4 rounded-lg bg-gray-50/20 mt-4";
const legendClassName = "text-lg font-semibold text-gray-800 px-2";
const fieldWrapperClass = "py-3 border-b border-gray-200/60 grid grid-cols-1 sm:grid-cols-2 sm:gap-4 items-center";
const labelClass = "text-sm font-medium text-gray-700 flex items-center mb-2 sm:mb-0";
const requiredMarker = <span className="text-red-500 mr-1">*</span>;
const numericInputClass = "w-full sm:max-w-xs px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";
const selectClassName = "w-full sm:max-w-xs px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400";
const photoCaptureContainerClass = "p-4 border border-gray-200 rounded-lg bg-gray-50/30 space-y-2 mt-4 sm:col-span-2";
const photoButtonClass = "w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 transition-all duration-200";

// Reusable Field Components
const BooleanField = React.memo(({ label, fieldName, value, onUpdate }: { label: string; fieldName: any; value: boolean | null; onUpdate: (field: any, val: boolean) => void; }) => (
    <div className={fieldWrapperClass}>
      <label className={labelClass}>{requiredMarker}{label}</label>
      <div className="flex space-x-2 sm:justify-self-end">
        <button type="button" onClick={() => onUpdate(fieldName, true)} className={`px-6 py-2 rounded-md ${value === true ? 'bg-green-600 text-white' : 'bg-gray-200'}`}>Yes</button>
        <button type="button" onClick={() => onUpdate(fieldName, false)} className={`px-6 py-2 rounded-md ${value === false ? 'bg-red-600 text-white' : 'bg-gray-200'}`}>No</button>
      </div>
    </div>
));

const ThreeStateField = React.memo(({ label, fieldName, value, onUpdate }: { label: string; fieldName: keyof MeasureSpecificationFormData; value: 'na' | 'yes' | 'no' | null; onUpdate: (field: keyof MeasureSpecificationFormData, val: 'na' | 'yes' | 'no' | null) => void; }) => (
    <div className={fieldWrapperClass}>
      <label className={labelClass}>{requiredMarker}{label}</label>
      <div className="flex space-x-2 sm:justify-self-end">
        <button type="button" onClick={() => onUpdate(fieldName, 'na')} className={`px-4 py-2 rounded-md ${value === 'na' ? 'bg-gray-400 text-white' : 'bg-gray-200'}`}>N/A</button>
        <button type="button" onClick={() => onUpdate(fieldName, 'yes')} className={`px-4 py-2 rounded-md ${value === 'yes' ? 'bg-green-600 text-white' : 'bg-gray-200'}`}>Yes</button>
        <button type="button" onClick={() => onUpdate(fieldName, 'no')} className={`px-4 py-2 rounded-md ${value === 'no' ? 'bg-red-600 text-white' : 'bg-gray-200'}`}>No</button>
      </div>
    </div>
));

const InputField = React.memo(({ label, name, value, placeholder, onChange, type = 'number' }: { label: string; name: keyof MeasureSpecificationFormData; value: string; placeholder: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; type?: string; }) => (
      <div className={fieldWrapperClass}>
          <label htmlFor={name} className={labelClass}>{requiredMarker}{label}</label>
          <input type={type} id={name} name={name} value={value} onChange={onChange} placeholder={placeholder} className={`${numericInputClass} sm:justify-self-end`} required />
      </div>
));

const SelectField = React.memo(({ label, name, value, options, onChange }: { label: string; name: keyof MeasureSpecificationFormData; value: string; options: string[]; onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void; }) => (
      <div className={fieldWrapperClass}>
          <label htmlFor={name} className={labelClass}>{requiredMarker}{label}</label>
          <select id={name} name={name} value={value} onChange={onChange} className={`${selectClassName} sm:justify-self-end`} required>
              <option value="">Select List Value(s)</option>
              {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
      </div>
));

const StyledBooleanField = React.memo(({ label, fieldName, value, onUpdate, yesLabel="YES", noLabel="" }: { label: string; fieldName: any; value: boolean | null; onUpdate: (field: any, val: boolean) => void; yesLabel?: string; noLabel?: string; }) => (
    <div className={fieldWrapperClass}>
      <label className={labelClass}>{requiredMarker}{label}</label>
      <div className="flex rounded-lg shadow-sm sm:justify-self-end">
        <button type="button" onClick={() => onUpdate(fieldName, true)} className={`px-6 py-2 rounded-l-md text-sm font-medium border ${value === true ? 'bg-blue-600 text-white z-10 border-blue-700' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`}>{yesLabel}</button>
        <button type="button" onClick={() => onUpdate(fieldName, false)} className={`-ml-px px-6 py-2 rounded-r-md text-sm font-medium border ${value === false || value === null ? 'bg-gray-200 text-gray-500 z-10 border-gray-300' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`}>{noLabel}</button>
      </div>
    </div>
));

const RadioGroupField = React.memo(({ label, name, value, options, onChange }: { label: string; name: any; value: number; options: { value: number; label: string }[]; onChange: (name: any, value: number) => void; }) => (
    <div className={fieldWrapperClass}>
        <label className={labelClass}>{requiredMarker}{label}</label>
        <div className="flex items-center space-x-4 sm:justify-self-end">
            {options.map(opt => (
                <label key={opt.value} className="flex items-center space-x-2">
                    <input type="radio" name={name} value={opt.value} checked={value === opt.value} onChange={() => onChange(name, opt.value)} className="h-5 w-5 text-yellow-500 focus:ring-yellow-400 border-gray-300" />
                    <span>{opt.label}</span>
                </label>
            ))}
        </div>
    </div>
));

const PhotoField = React.memo(({ label, photoType, altText, photoData, onPhotosCaptured, removePhoto, maxPhotos = 1, id }: { label: string; photoType: any; altText: string; photoData: DisplayablePhotoData | null | DisplayablePhotoData[]; onPhotosCaptured: (photoType: any, capturedPhotos: PhotoData[], id?:number) => void; removePhoto: (photoType: any, displayId?: string, id?: number) => void; maxPhotos?: number; id?: number }) => {
    const renderPhotoThumbnail = (pData: DisplayablePhotoData | null, pType: keyof MeasureSpecificationPhotos, id?: number) => {
        if (!pData) return null;
        // Use CDN URL if available, otherwise use dataUrl
        const imageUrl = pData.url || pData.dataUrl;
        if (!imageUrl) return null;
        
        return (
          <div className="w-24 h-24 relative group flex-shrink-0 mt-2">
            <img src={imageUrl} alt={altText} className="w-full h-full object-cover rounded-lg border-2 border-yellow-300 shadow-sm" />
            <button type="button" onClick={() => removePhoto(pType, pData.displayId, id)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity" title={`Remove ${altText}`}>X</button>
          </div>
        );
    };

    const handleCapture = (capturedPhotos: PhotoData[]) => {
        onPhotosCaptured(photoType, capturedPhotos, id);
    };

    return (
     <div className={photoCaptureContainerClass}>
        <label className={labelClass}>{requiredMarker}{label}</label>
        <PhotoCapture onPhotosCaptured={handleCapture} maxPhotos={maxPhotos} buttonLabel={`Capture ${altText}`} buttonClass={photoButtonClass} />
        {maxPhotos > 1 
         ? (photoData as DisplayablePhotoData[] || []).map(pData => {
              // Use CDN URL if available, otherwise use dataUrl
              const imageUrl = pData.url || pData.dataUrl;
              if (!imageUrl) return null;
              
              return (
                <div key={pData.displayId} className="w-24 h-24 relative group flex-shrink-0">
                  <img src={imageUrl} alt={altText} className="w-full h-full object-cover rounded-lg border-2 border-yellow-300 shadow-sm" />
                  <button type="button" onClick={() => removePhoto(photoType, pData.displayId, id)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity" title={`Remove ${altText}`}>X</button>
                </div>
              );
            })
         : renderPhotoThumbnail(photoData as DisplayablePhotoData | null, photoType, id)
        }
    </div>
    );
});

const ThreeStateButton = React.memo(({ label, value, onClick }: { label: string; value: string; onClick: (value: string) => void; }) => {
    const baseClasses = "px-4 py-2 rounded-md text-sm font-medium border";
    const selectedClasses = "bg-blue-600 text-white z-10 border-blue-700";
    const unselectedClasses = "bg-white text-gray-700 hover:bg-gray-50 border-gray-300";
    return (
        <button type="button" onClick={() => onClick(label)} className={`${baseClasses} ${value === label ? selectedClasses : unselectedClasses}`}>{label}</button>
    );
});

const MeasureSpecificationForm: React.FC<MeasureSpecificationFormProps> = ({ 
  onNavigateNext, 
  onNavigateBack, 
  floorPlanId,
  surveyData,
  surveyId,
  onDataUpdate,
  onSaveImmediately
}) => {
  // Initialize photo upload queue with explicit surveyId and property name for better file organization
  const propertyName = surveyData ? getRetrofitSurveyAddress(surveyData) : undefined;
  const { queuePhotoUpload } = useRetrofitPhotoUpload(
    surveyId || surveyData?.id || floorPlanId || undefined,
    propertyName
  );

  const [formData, setFormData] = useState<MeasureSpecificationFormData>({
    ...initialFormData,
    ...surveyData?.measure_specification
  });
  const [photos, setPhotos] = useState<MeasureSpecificationPhotos>(initialPhotos);
  const [selectedGallery, setSelectedGallery] = useState<string | null>(null);
  const [showAngleMeasurer, setShowAngleMeasurer] = useState(false);
  const [roofPitchMode, setRoofPitchMode] = useState<'tool' | 'manual'>('manual');

  // Wall Insulation elevation state (retrofit-specific)
  const [retrofitElevationData, setRetrofitElevationData] = useState<Record<string, {
    wallArea: number;
    untreatableArea: number;
    treatableArea: number;
    dimensions: { height: number; width: number };
    elements: {
      doors: { id: string; height: number; width: number }[];
      windows: { id: string; height: number; width: number }[];
      airVents: { id: string }[];
    };
    placedElements?: {
      id: string;
      type: string;
      x: number;
      y: number;
      elevation: string;
      height?: number;
      width?: number;
    }[];
    wallInsulationType?: string;
  }>>({});

  // Global calculations for retrofit wall insulation
  const retrofitGlobalCalculations = useMemo(() => {
    let totalDwellingArea = 0;
    let totalUntreatableArea = 0;
    let totalTreatableArea = 0;

    Object.values(retrofitElevationData).forEach(e => {
      totalDwellingArea += e.wallArea || 0;
      totalUntreatableArea += e.untreatableArea || 0;
      totalTreatableArea += e.treatableArea || 0;
    });

    const totalWallAreaWholeHouse = totalDwellingArea;
    const poptPercentage =
      totalWallAreaWholeHouse > 0
        ? (totalTreatableArea / totalWallAreaWholeHouse) * 100
        : 0;

    return {
      totalDwellingArea,
      totalUntreatableArea,
      totalTreatableArea,
      totalWallAreaWholeHouse,
      poptPercentage,
    };
  }, [retrofitElevationData]);

  // Track last restored elevation data to detect when we need to re-restore
  const lastRestoredElevationsRef = useRef<string>('');
  
  // Track if we're actively modifying elevation data (prevent restoration during edits)
  const isActivelyEditingRef = useRef<boolean>(false);
  
  // Track if restoration has run for current mount (prevent loops)
  const hasRestoredThisMountRef = useRef<boolean>(false);
  
  // Track if we're currently restoring (prevent persistence during restoration)
  const isRestoringRef = useRef<boolean>(false);
  
  // Track restoration count to force diagram remount only when data is restored
  const [restorationCount, setRestorationCount] = useState(0);

  // Room in Roof (RIR) diagram data tracking (retrofit-specific)
  const [retrofitRirData, setRetrofitRirData] = useState<{
    measurements: Array<{
      id: string;
      label: string;
      value: string;
      unit: string;
      length?: string;
      height?: string;
      insulation?: string;
      insulationType?: string;
      visible?: boolean;
    }>;
    annotations: Array<{
      id: string;
      x: number;
      y: number;
      text: string;
    }>;
    insulationType?: string;
    ventilationType?: string;
    diagramType?: 'standard' | 'with-dormer';
    diagramImage?: string;
  }>({
    measurements: [],
    annotations: [],
    insulationType: 'between-rafters',
    ventilationType: 'ridge-and-eaves',
    diagramType: 'standard',
    diagramImage: ''
  });

  // Section-based autosave: saves only measure_specification section (not entire survey)
  // This reduces IO from 6MB to ~100-800KB per save, enabling better scalability
  const { saveNow: saveMeasureSpecSection } = useSectionAutosave({
    surveyId: surveyId || surveyData?.id,
    sectionData: {
      ...formData,
      wallInsulationElevations: surveyData?.measure_specification?.wallInsulationElevations || [],
      wallInsulationGlobalCalculations: retrofitGlobalCalculations,
      roomInRoofData: retrofitRirData,
      photos: photos
    },
    sectionName: 'measure_specification',
    enabled: true,
    debounceMs: 3000, // Save 3 seconds after last change
    minIntervalMs: 10000 // Min 10 seconds between saves
  });

  // Initialize on mount: clear editing flag and prepare for restoration
  useEffect(() => {
    isActivelyEditingRef.current = false;
    hasRestoredThisMountRef.current = false; // Reset restoration flag for new mount
    
    // Set restoring flag to prevent persistence during mount/restoration
    isRestoringRef.current = true;
    
    console.log('🔄 Component mounted, ready for restoration');
    
    // Clear restoring flag after mount completes
    setTimeout(() => {
      isRestoringRef.current = false;
      console.log('✅ Mount complete, allowing persistence');
    }, 200);
  }, []);

  // Restore suitableMeasures from loaded survey
  useEffect(() => {
    if (surveyData?.measure_specification.suitableMeasures) {
      setFormData(prev => ({
        ...prev,
        suitableMeasures: surveyData.measure_specification.suitableMeasures || []
      }));
    }
  }, [surveyData?.measure_specification.suitableMeasures]);

  // Restore RIR data from loaded survey (only once per mount)
  const hasRestoredRirData = useRef(false);
  useEffect(() => {
    if (surveyData?.measure_specification.roomInRoofData && !hasRestoredRirData.current) {
      setRetrofitRirData(surveyData.measure_specification.roomInRoofData);
      hasRestoredRirData.current = true;
    }
    
    // Reset flag on unmount so data can be restored when navigating back
    return () => {
      hasRestoredRirData.current = false;
    };
  }, [surveyData?.measure_specification.roomInRoofData]);

  // Restore RIR and Loft photos from loaded survey
  useEffect(() => {
    if (surveyData?.measure_specification) {
      const ms = surveyData.measure_specification;
      const photoFields: (keyof MeasureSpecificationPhotos)[] = [
        // RIR Photos
        'rirExteriorElevations',
        'rirInternalRoomPhotos',
        'rirFlatCeilingInsulation',
        'rirSlopingCeilingInsulation',
        'rirStudWallsInsulation',
        'rirDormerPhotos',
        // Loft Survey Photos
        'mainPropertyInsulationDepthEvidence',
        'mainLoftHatchEvidence',
        'mainPropertyRoofPitchEvidence',
        'mainLoftAreaImages',
        'apexMeasurementEvidence',
        'partyWallEvidence',
        'downlightImage',
        'coldWaterTankPipeworkEvidence',
        'roofVentsImage'
      ];

      const restoredPhotos: Partial<MeasureSpecificationPhotos> = {};
      for (const field of photoFields) {
        const fieldValue = (ms as any)[field];
        if (fieldValue) {
          restoredPhotos[field] = fieldValue;
        }
      }

      if (Object.keys(restoredPhotos).length > 0) {
        setPhotos(prev => ({
          ...prev,
          ...restoredPhotos
        }));
      }
    }
  }, [surveyData?.measure_specification]);

  // Handler for RIR data changes
  const handleRetrofitRirDataChange = useCallback((data: typeof retrofitRirData) => {
    setRetrofitRirData(data);
  }, []);

  // Persist RIR data to measure_specification (with debouncing to prevent rapid updates)
  useEffect(() => {
    if (!onDataUpdate || !surveyData) return;
    if (!retrofitRirData.diagramImage && retrofitRirData.measurements.length === 0) return;

    // Debounce saves to prevent rapid cascading updates
    const saveTimer = setTimeout(() => {
      onDataUpdate(prev => ({
        ...prev,
        measure_specification: {
          ...prev.measure_specification,
          roomInRoofData: retrofitRirData
        }
      }), 'measure_specification');
    }, 500); // 500ms debounce

    return () => clearTimeout(saveTimer);
  }, [retrofitRirData, onDataUpdate, surveyData]);

  // Restore placed elements and elevation data from saved survey
  useEffect(() => {
    if (!surveyData?.measure_specification.wallInsulationElevations) return;
    
    // Create a fingerprint of the current elevations data to detect changes
    const currentFingerprint = JSON.stringify(
      surveyData.measure_specification.wallInsulationElevations.map(e => ({
        type: e.type,
        placedElementsCount: e.placedElements?.length || 0,
        hasCalculations: !!e.calculations
      }))
    );
    
    const fingerprintMatch = currentFingerprint === lastRestoredElevationsRef.current;
    const hasEmptyState = Object.keys(retrofitElevationData).length === 0;
    
    console.log('🔍 Restoration check:', {
      isActivelyEditing: isActivelyEditingRef.current,
      hasRestoredThisMount: hasRestoredThisMountRef.current,
      hasEmptyState,
      currentFingerprint: currentFingerprint.substring(0, 100),
      lastFingerprint: lastRestoredElevationsRef.current.substring(0, 100),
      fingerprintMatch,
      willRestore: !isActivelyEditingRef.current && !hasRestoredThisMountRef.current && !fingerprintMatch
    });
    
    // Don't restore if we're actively editing (prevents race conditions during placement)
    if (isActivelyEditingRef.current) {
      console.log('⏸️ Skipping restoration - active editing in progress');
      return;
    }
    
    // Don't restore if we've already restored once this mount (prevents infinite loops)
    if (hasRestoredThisMountRef.current) {
      console.log('⏸️ Skipping restoration - already restored this mount cycle');
      return;
    }
    
    // On fresh mount, always check if there's data to restore
    // The mount-cycle flag above is sufficient to prevent loops
    // Fingerprint tracking is only used for logging, not for skipping restoration
    
    const restoredData: Record<string, any> = {};
    
    console.log('🔎 Examining survey data for restoration:', {
      elevationCount: surveyData.measure_specification.wallInsulationElevations.length,
      elevations: surveyData.measure_specification.wallInsulationElevations.map(e => ({
        type: e.type,
        hasCalculations: !!e.calculations,
        hasPlacedElements: !!e.placedElements,
        placedElementsCount: e.placedElements?.length || 0,
        placedElementsData: e.placedElements
      }))
    });
    
    surveyData.measure_specification.wallInsulationElevations.forEach(elevation => {
      if (elevation.calculations || elevation.placedElements) {
        restoredData[elevation.type] = {
          wallArea: elevation.calculations?.totalWallArea || 0,
          untreatableArea: elevation.calculations?.totalElementsArea || 0,
          treatableArea: elevation.calculations?.treatableArea || 0,
          placedElements: elevation.placedElements || [],
          wallInsulationType: elevation.wallInsulationType,
        };
      }
    });
    
    console.log('🔎 Restored data built:', {
      restoredTypes: Object.keys(restoredData),
      totalPlacedElements: Object.values(restoredData).reduce((sum: number, data: any) => 
        sum + (data.placedElements?.length || 0), 0)
    });
    
    // Mark restoration as attempted for this mount regardless of whether data exists
    hasRestoredThisMountRef.current = true;
    
    if (Object.keys(restoredData).length > 0) {
      console.log('📥 Restoring elevation data including placed elements:', {
        elevationCount: Object.keys(restoredData).length,
        placedElementsCount: Object.values(restoredData).reduce((sum: number, data: any) => 
          sum + (data.placedElements?.length || 0), 0)
      });
      
      // Set restoring flag to prevent persistence during restoration
      isRestoringRef.current = true;
      setRetrofitElevationData(restoredData);
      lastRestoredElevationsRef.current = currentFingerprint;
      
      // Increment restoration count to force diagram remount
      setRestorationCount(prev => prev + 1);
      console.log('🔄 Incrementing restoration count to force diagram remount');
      
      // Clear restoring flag after state update completes
      setTimeout(() => {
        isRestoringRef.current = false;
        console.log('✅ Restoration complete, allowing persistence');
      }, 100);
    } else {
      console.log('ℹ️ No elevation data to restore from survey');
    }
  }, [surveyData?.measure_specification.wallInsulationElevations, formData.wallInsulationType]);

  // Handler for retrofit elevation data changes
  const handleRetrofitElevationDataChange = useCallback((elevationId: string | number, data: any) => {
    console.log('✏️ Elevation data change detected:', {
      elevationId: elevationId,
      hasPlacedElements: !!data.placedElements,
      placedElementsCount: data.placedElements?.length || 0,
      placedElementsData: data.placedElements,
      dataKeys: Object.keys(data)
    });
    isActivelyEditingRef.current = true;
    
    setRetrofitElevationData(prev => ({
      ...prev,
      [elevationId]: {
        ...prev[elevationId],
        ...data,
        wallInsulationType: formData.wallInsulationType,
      },
    }));
    
    // Clear the editing flag after save has time to complete (5 seconds)
    setTimeout(() => {
      console.log('✅ Clearing active editing flag');
      isActivelyEditingRef.current = false;
    }, 5000);
  }, [formData.wallInsulationType]);

  // Persist retrofit wall insulation elevation data to measure_specification
  useEffect(() => {
    if (!onDataUpdate) return;
    if (Object.keys(retrofitElevationData).length === 0) return;
    
    // Don't persist during restoration (prevents clearing data on mount)
    if (isRestoringRef.current) {
      console.log('⏸️ Skipping persistence - restoration in progress');
      return;
    }

    const updatedElevations =
      (surveyData?.measure_specification.wallInsulationElevations || [])
        .map(e => {
          const dataForType = retrofitElevationData[e.type];
          if (dataForType) {
            return {
              ...e,
              dimensions: dataForType.dimensions,
              elements: dataForType.elements,
              calculations: {
                totalWallArea: dataForType.wallArea,
                totalElementsArea: dataForType.untreatableArea,
                treatableArea: dataForType.treatableArea,
              },
              placedElements: dataForType.placedElements,
              wallInsulationType: dataForType.wallInsulationType,
            };
          }
          return e;
        });

    // Update fingerprint to match what we're about to save
    // This prevents restoration from triggering when save completes
    const newFingerprint = JSON.stringify(
      updatedElevations.map(e => ({
        type: e.type,
        placedElementsCount: e.placedElements?.length || 0,
        hasCalculations: !!e.calculations
      }))
    );
    lastRestoredElevationsRef.current = newFingerprint;
    
    console.log('💾 Persisting elevation data:', {
      elevationCount: updatedElevations.length,
      placedElementsCount: updatedElevations.reduce((sum, e) => sum + (e.placedElements?.length || 0), 0),
      isActivelyEditing: isActivelyEditingRef.current,
      persistenceTime: new Date().toLocaleTimeString(),
      newFingerprint: newFingerprint.substring(0, 100)
    });

    onDataUpdate(prev => ({
      ...prev,
      measure_specification: {
        ...prev.measure_specification,
        wallInsulationType: formData.wallInsulationType,
        wallInsulationElevations: updatedElevations,
        wallInsulationGlobalCalculations: retrofitGlobalCalculations,
      },
    }), 'measure_specification');
  }, [retrofitElevationData, retrofitGlobalCalculations, formData.wallInsulationType, onDataUpdate]);

  // Debounce timer for dropdown autosave
  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Track recent changes to prevent sync during immediate autosaves
  const recentChangeRef = useRef(false);
  // Track last synced data to prevent loops
  const lastSyncedDataRef = useRef<string>('');
  
  // Sync surveyData prop changes back to form state (but not while autosave is pending or recently changed)
  useEffect(() => {
    // Don't sync if we have a pending autosave or just made a change
    if (surveyData?.measure_specification && !autosaveTimerRef.current && !recentChangeRef.current) {
      const incoming = JSON.stringify(surveyData.measure_specification);
      
      if (incoming !== lastSyncedDataRef.current) {
        lastSyncedDataRef.current = incoming;
        setFormData(prev => ({
          ...prev,
          ...surveyData.measure_specification
        }));
      }
    }
  }, [surveyData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Mark recent change to prevent sync
    recentChangeRef.current = true;
    
    // Clear system-specific fields when switching heating systems
    if (name === 'postHeatingSystem') {
      const isCombustionSystem = value === "Condensing Gas Boiler" || value === "ASHP/Gas Boiler Hybrid";
      const isESH = value === "ESH";
      
      if (isESH) {
        // Clear ALL system-specific fields for ESH (storage heaters)
        setFormData(prev => ({
          ...prev,
          [name]: value,
          flueType: '',
          flueGuardRequired: null,
          flueFittingKitRequired: null,
          plumeKitRequired: null,
          condenseTermination: '',
          gasMeterBoilerDistance: '',
          frostProtectionRequired: null,
          remedialWorksToBrickwork: null,
          newBoilerLocation: '',
          weatherCompensatorNeeded: null,
          loadCompensatorNeeded: null,
          trvsRequired: null,
          lockShieldsRequired: null,
          pipeworkInUnheatedAreas: null
        }));
        
        // Update parent with cleared fields
        if (onDataUpdate) {
          onDataUpdate(prev => ({
            ...prev,
            measure_specification: {
              ...prev.measure_specification,
              [name]: value,
              flueType: '',
              flueGuardRequired: null,
              flueFittingKitRequired: null,
              plumeKitRequired: null,
              condenseTermination: '',
              gasMeterBoilerDistance: '',
              frostProtectionRequired: null,
              remedialWorksToBrickwork: null,
              newBoilerLocation: '',
              weatherCompensatorNeeded: null,
              loadCompensatorNeeded: null,
              trvsRequired: null,
              lockShieldsRequired: null,
              pipeworkInUnheatedAreas: null
            }
          }), 'measure_specification');
        }
      } else if (!isCombustionSystem) {
        // Clear flue-related and gas fields for Electric Boiler & ASHP (keep wet system fields)
        setFormData(prev => ({
          ...prev,
          [name]: value,
          flueType: '',
          flueGuardRequired: null,
          flueFittingKitRequired: null,
          plumeKitRequired: null,
          condenseTermination: '',
          gasMeterBoilerDistance: ''
        }));
        
        // Update parent with cleared fields
        if (onDataUpdate) {
          onDataUpdate(prev => ({
            ...prev,
            measure_specification: {
              ...prev.measure_specification,
              [name]: value,
              flueType: '',
              flueGuardRequired: null,
              flueFittingKitRequired: null,
              plumeKitRequired: null,
              condenseTermination: '',
              gasMeterBoilerDistance: ''
            }
          }), 'measure_specification');
        }
      } else {
        // Normal update for combustion systems
        setFormData(prev => ({ ...prev, [name]: value }));
        
        if (onDataUpdate) {
          onDataUpdate(prev => ({
            ...prev,
            measure_specification: {
              ...prev.measure_specification,
              [name]: value
            }
          }), 'measure_specification');
        }
      }
    } else {
      // Normal update for other fields
      setFormData(prev => ({ ...prev, [name]: value }));
      
      // Trigger immediate autosave via parent callback (no debouncing)
      if (onDataUpdate) {
        onDataUpdate(prev => ({
          ...prev,
          measure_specification: {
            ...prev.measure_specification,
            [name]: value
          }
        }), 'measure_specification');
      }
    }
    
    // Allow sync after autosave completes
    setTimeout(() => {
      recentChangeRef.current = false;
    }, 100);
  };

  // Handle blur event - save immediately when user leaves input
  const handleInputBlur = () => {
    if (onSaveImmediately) {
      onSaveImmediately().catch(err => 
        console.error('[MeasureSpecificationForm] Immediate save on blur failed:', err)
      );
    }
  };

  // Save immediately when component unmounts (section navigation)
  useEffect(() => {
    return () => {
      console.log('🔄 Component unmounting, force persisting current state');
      
      // Clear editing flag on unmount
      isActivelyEditingRef.current = false;
      
      // Force immediate persist of current elevation data before unmount
      if (onDataUpdate && Object.keys(retrofitElevationData).length > 0) {
        const updatedElevations =
          (surveyData?.measure_specification.wallInsulationElevations || [])
            .map(e => {
              const dataForType = retrofitElevationData[e.type];
              if (dataForType) {
                return {
                  ...e,
                  dimensions: dataForType.dimensions,
                  elements: dataForType.elements,
                  calculations: {
                    totalWallArea: dataForType.wallArea,
                    totalElementsArea: dataForType.untreatableArea,
                    treatableArea: dataForType.treatableArea,
                  },
                  placedElements: dataForType.placedElements,
                  wallInsulationType: dataForType.wallInsulationType,
                };
              }
              return e;
            });
        
        console.log('💾 [UNMOUNT] Force persisting elevation data:', {
          elevationCount: updatedElevations.length,
          placedElementsCount: updatedElevations.reduce((sum, e) => sum + (e.placedElements?.length || 0), 0)
        });
        
        onDataUpdate(prev => ({
          ...prev,
          measure_specification: {
            ...prev.measure_specification,
            wallInsulationType: formData.wallInsulationType,
            wallInsulationElevations: updatedElevations,
            wallInsulationGlobalCalculations: retrofitGlobalCalculations,
          },
        }), 'measure_specification');
      }
      
      // Note: Removed onSaveImmediately call here because it triggers full survey saves
      // which fail when large base64 data (like RIR diagrams) is present.
      // Section-specific partial saves (via useSectionAutosave) already handle persistence correctly.
    };
  }, [onDataUpdate, retrofitElevationData, surveyData?.measure_specification.wallInsulationElevations, retrofitRirData]);

  // 📊 FILE SIZE ANALYSIS: Photo fields for this section
  const MEASURE_SPEC_PHOTO_FIELDS = [
    // Heating Controls
    'preExistingHeatingControlsImages',
    'bypassRadiatorImage',
    // Loft Survey
    'mainPropertyInsulationDepthEvidence',
    'mainLoftHatchEvidence',
    'mainPropertyRoofPitchEvidence',
    'loftGeneralConditionImage',
    'fireResistingCeilingEvidence',
    'downlightImage',
    'coldWaterTankPipeworkEvidence',
    'roofVentsImage',
    // Heating System
    'imageOfCurrentBoiler',
    'boilerDataBadge',
    'imageOfFluePipe',
    'imageOfCondensatePipe',
    'stopTapImages',
    // Room in Roof (RIR)
    'rirExteriorElevations',
    'rirInternalRoomPhotos',
    'rirFlatCeilingInsulation',
    'rirSlopingCeilingInsulation',
    'rirStudWallsInsulation',
    'rirDormerPhotos',
    'roofPitchEvidenceRIR',
    'downlightsEvidence',
    'roofInRoofCorner1',
    'roofInRoofCorner2',
    'roofInRoofCorner3',
    'roofInRoofCorner4',
    'studWallImages'
  ];

  // 📊 FILE SIZE ANALYSIS: Analyze measure_specification section
  const analyzeMeasureSpecFileSize = useCallback(() => {
    const result = analyzeSectionFileSize(
      'measure_specification',
      surveyData?.measure_specification,
      MEASURE_SPEC_PHOTO_FIELDS
    );
    logFileSizeAnalysis('measure_specification', result);
  }, [surveyData?.measure_specification]);

  // Run analysis on data load
  useEffect(() => {
    if (surveyData?.measure_specification) {
      const timer = setTimeout(() => analyzeMeasureSpecFileSize(), 1000);
      return () => clearTimeout(timer);
    }
  }, [surveyData?.measure_specification, analyzeMeasureSpecFileSize]);

  // Expose to window for manual testing
  useEffect(() => {
    (window as any).analyzeMeasureSpecification = analyzeMeasureSpecFileSize;
    return () => {
      delete (window as any).analyzeMeasureSpecification;
    };
  }, [analyzeMeasureSpecFileSize]);

  // 🧹 CLEANUP: Strip base64 from photos that already have CDN URLs
  const cleanupBase64FromCDNPhotos = useCallback(() => {
    console.log('🧹 [BASE64 CLEANUP - MEASURE_SPEC] Checking for photos with CDN URLs that still have base64...');
    
    let cleanupNeeded = false;
    const cleanedPhotos = { ...photos };
    
    Object.keys(cleanedPhotos).forEach((fieldName) => {
      const field = cleanedPhotos[fieldName as keyof MeasureSpecificationPhotos];
      if (!field) return;
      
      // Handle both array and single photo fields
      if (Array.isArray(field)) {
        const cleaned = field.map((photo: any) => {
          if (photo && photo.url && photo.url.startsWith('http') && (photo.dataUrl || photo.base64String)) {
            console.log(`🧹 [BASE64 CLEANUP - MEASURE_SPEC] Stripping base64 from ${fieldName}/${photo.displayId}`);
            cleanupNeeded = true;
            return {
              displayId: photo.displayId,
              format: photo.format,
              fileName: photo.fileName,
              url: photo.url,
              uploading: false,
              error: undefined
              // base64 fields NOT included
            };
          }
          return photo;
        });
        (cleanedPhotos as any)[fieldName] = cleaned;
      } else if (field && (field as any).url && (field as any).url.startsWith('http') && ((field as any).dataUrl || (field as any).base64String)) {
        console.log(`🧹 [BASE64 CLEANUP - MEASURE_SPEC] Stripping base64 from ${fieldName}`);
        cleanupNeeded = true;
        const photo = field as any;
        (cleanedPhotos as any)[fieldName] = {
          displayId: photo.displayId,
          format: photo.format,
          fileName: photo.fileName,
          url: photo.url,
          uploading: false,
          error: undefined
        };
      }
    });
    
    if (cleanupNeeded) {
      console.log('🧹 [BASE64 CLEANUP - MEASURE_SPEC] ✅ Cleaned photos, updating state and saving...');
      setPhotos(cleanedPhotos);
      
      // Save cleaned photos to database
      if (onDataUpdate) {
        setTimeout(() => {
          console.log('🧹 [BASE64 CLEANUP - MEASURE_SPEC] 💾 Saving cleaned photos to database');
          onDataUpdate(prevData => ({
            ...prevData,
            measure_specification: {
              ...prevData.measure_specification,
              ...formData,
              ...cleanedPhotos
            }
          }), 'measure_specification');
        }, 100);
      }
    } else {
      console.log('🧹 [BASE64 CLEANUP - MEASURE_SPEC] No cleanup needed, all photos are clean');
    }
  }, [photos, formData, onDataUpdate]);

  // Expose cleanup function to window
  useEffect(() => {
    (window as any).cleanupMeasureSpecificationBase64 = cleanupBase64FromCDNPhotos;
    return () => {
      delete (window as any).cleanupMeasureSpecificationBase64;
    };
  }, [cleanupBase64FromCDNPhotos]);

  // 🧹 AUTO-CLEANUP: Automatically clean base64 from photos with CDN URLs on page load
  useEffect(() => {
    if (!photos || !onDataUpdate) return;
    
    // Check if any photos have CDN URLs but still have base64
    const needsCleanup = Object.values(photos).some(field => {
      if (Array.isArray(field)) {
        return field.some(p => p && p.url && p.url.startsWith('http') && (p.dataUrl || p.base64String));
      } else if (field) {
        const photo = field as any;
        return photo.url && photo.url.startsWith('http') && (photo.dataUrl || photo.base64String);
      }
      return false;
    });
    
    if (needsCleanup) {
      console.log('🧹 [AUTO-CLEANUP - MEASURE_SPEC] Detected photos with CDN + base64, running automatic cleanup in 3 seconds...');
      const timer = setTimeout(() => {
        cleanupBase64FromCDNPhotos();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [photos, onDataUpdate, cleanupBase64FromCDNPhotos]);
  
  // 🗑️ LEGACY CLEANUP: Remove old photo field names if they exist
  useEffect(() => {
    if (!surveyData?.measure_specification || !onDataUpdate) return;
    
    const data = surveyData.measure_specification as any;
    const legacyFields = ['mainLoftAreaImages', 'apexMeasurementEvidence', 'partyWallEvidence'];
    let hasLegacyFields = false;
    
    legacyFields.forEach(fieldName => {
      if (data[fieldName]) {
        console.warn(`🗑️ [LEGACY CLEANUP - MEASURE_SPEC] Found old ${fieldName} field:`, 
          `${(JSON.stringify(data[fieldName]).length / 1024).toFixed(2)} KB`);
        hasLegacyFields = true;
      }
    });
    
    if (hasLegacyFields) {
      const cleanedData = { ...data };
      legacyFields.forEach(fieldName => delete cleanedData[fieldName]);
      
      // Save cleaned data
      onDataUpdate(prevData => ({
        ...prevData,
        measure_specification: cleanedData
      }), 'measure_specification');
      
      console.log('🗑️ [LEGACY CLEANUP - MEASURE_SPEC] ✅ Removed legacy photo fields');
    }
  }, [surveyData?.measure_specification, onDataUpdate]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    
    // Mark recent change to prevent sync
    recentChangeRef.current = true;
    setFormData(prev => {
      const newMeasures = checked
        ? [...prev.suitableMeasures, name]
        : prev.suitableMeasures.filter(m => m !== name);
      return { ...prev, suitableMeasures: newMeasures };
    });
    
    // Trigger autosave via parent callback
    if (onDataUpdate) {
      const newMeasures = checked
        ? [...formData.suitableMeasures, name]
        : formData.suitableMeasures.filter(m => m !== name);
      onDataUpdate(prev => ({
        ...prev,
        measure_specification: {
          ...prev.measure_specification,
          suitableMeasures: newMeasures
        }
      }), 'measure_specification');
      // Allow sync after autosave completes
      setTimeout(() => {
        recentChangeRef.current = false;
      }, 100);
    }
  };

  const handleBooleanButtonClick = (fieldName: keyof MeasureSpecificationFormData, value: boolean | null) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    
    // Trigger autosave via parent callback
    if (onDataUpdate) {
      onDataUpdate(prev => ({
        ...prev,
        measure_specification: {
          ...prev.measure_specification,
          [fieldName]: value
        }
      }), 'measure_specification');
    }
  };
  
  const handleThreeStateButtonClick = (fieldName: keyof MeasureSpecificationFormData, value: 'na' | 'yes' | 'no' | null) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    
    // Trigger autosave via parent callback
    if (onDataUpdate) {
      onDataUpdate(prev => ({
        ...prev,
        measure_specification: {
          ...prev.measure_specification,
          [fieldName]: value
        }
      }), 'measure_specification');
    }
  };

  // Photo handling for RIR arrays
  const handleRIRPhotosCaptured = (photoType: keyof MeasureSpecificationPhotos, incomingPhotos: PhotoData[]) => {
    const newPhotoData: DisplayablePhotoData[] = incomingPhotos.map((photo) => {
      const displayId = `${photoType}_${Date.now()}_${Math.random()}`;
      const dataUrl = `data:image/${photo.format};base64,${photo.base64String}`;

      return {
        ...photo,
        displayId,
        dataUrl,
        uploading: true,
        fileName: undefined,
        url: undefined,
        error: undefined
      };
    });

    setPhotos(prev => {
      const existingPhotos = Array.isArray(prev[photoType]) ? prev[photoType] as DisplayablePhotoData[] : [];
      const updatedPhotos = {
        ...prev,
        [photoType]: [...existingPhotos, ...newPhotoData]
      };

      // Queue photos for background upload to Supabase
      newPhotoData.forEach(photo => {
        queuePhotoUpload(
          photo.displayId,
          photo.dataUrl,
          'measure_specification',
          photoType,
          // On success: update photo with URL
          async (url: string) => {
            setPhotos(current => {
              const currentPhotos = Array.isArray(current[photoType]) ? current[photoType] as DisplayablePhotoData[] : [];
              const updatedPhotos = currentPhotos.map(p =>
                p.displayId === photo.displayId
                  ? { ...p, url, uploading: false, error: undefined, dataUrl: undefined }
                  : p
              );
              return {
                ...current,
                [photoType]: updatedPhotos
              };
            });

            // Save to DB with CDN URL
            if (onDataUpdate) {
              setTimeout(() => {
                setPhotos(currentPhotos => {
                  onDataUpdate(prevData => ({
                    ...prevData,
                    measure_specification: {
                      ...prevData.measure_specification,
                      [photoType]: (currentPhotos[photoType] as DisplayablePhotoData[])
                    }
                  }), 'measure_specification');
                  return currentPhotos;
                });
              }, 0);
            }
          },
          // On error: mark photo with error
          (error: string) => {
            setPhotos(current => {
              const currentPhotos = Array.isArray(current[photoType]) ? current[photoType] as DisplayablePhotoData[] : [];
              return {
                ...current,
                [photoType]: currentPhotos.map(p =>
                  p.displayId === photo.displayId
                    ? { ...p, uploading: false, error }
                    : p
                )
              };
            });
          }
        );
      });

      return updatedPhotos;
    });
  };

  const removeRIRPhoto = async (photoType: keyof MeasureSpecificationPhotos, displayIdToRemove?: string) => {
    console.log(`[MeasureSpec] 🗑️ Removing photo from ${photoType}, displayId: ${displayIdToRemove}`);
    
    if (displayIdToRemove) {
      // Find and delete from Supabase Storage
      const currentPhotoState = Array.isArray(photos[photoType]) ? photos[photoType] as DisplayablePhotoData[] : [];
      const photoToDelete = currentPhotoState.find(p => p.displayId === displayIdToRemove);
      
      if (photoToDelete) {
        let filePath = (photoToDelete as any).fileName;
        if (!filePath && photoToDelete.url?.includes('/storage/v1/object/public/')) {
          filePath = photoToDelete.url.split('/storage/v1/object/public/')[1]?.split('/').slice(1).join('/');
        }
        if (filePath) {
          try {
            await deletePhotoFromSupabase(filePath, 'retrofit-survey-images');
            console.log(`[MeasureSpec] ✅ Deleted from storage: ${filePath}`);
          } catch (error) {
            console.warn(`[MeasureSpec] ⚠️ Failed to delete from storage:`, error);
          }
        }
      }
    }
    
    setPhotos(prev => {
      const currentPhotoState = Array.isArray(prev[photoType]) ? prev[photoType] as DisplayablePhotoData[] : [];
      const filteredPhotos = displayIdToRemove
        ? currentPhotoState.filter(p => p.displayId !== displayIdToRemove)
        : [];
      const updatedPhotos = { ...prev, [photoType]: filteredPhotos };

      // Trigger immediate autosave
      if (onDataUpdate) {
        setTimeout(() => {
          onDataUpdate(prevData => ({
            ...prevData,
            measure_specification: {
              ...prevData.measure_specification,
              [photoType]: filteredPhotos
            }
          }), 'measure_specification');
        }, 0);
      }

      return updatedPhotos;
    });
  };

  const handleOpenGallery = (e: React.MouseEvent, photoType: string) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedGallery(photoType);
  };

  const handleCloseGallery = () => {
    setSelectedGallery(null);
  };

  const renderPhotoPreview = (photoDatas: DisplayablePhotoData[], photoType: keyof MeasureSpecificationPhotos, altTextPrefix: string) => {
    const photoArray = Array.isArray(photoDatas) ? photoDatas : [];
    if (photoArray.length === 0) return null;

    const latestPhoto = photoArray[photoArray.length - 1];
    const imageUrl = latestPhoto.url || latestPhoto.dataUrl;
    
    if (!imageUrl) return null;

    return (
      <button
        type="button"
        onClick={(e) => handleOpenGallery(e, photoType)}
        className="relative w-20 h-20 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:scale-105 ring-2 ring-slate-300 hover:ring-blue-400 flex-shrink-0 mt-2"
      >
        <img
          src={imageUrl}
          alt={altTextPrefix}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-1 right-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
          <Images size={12} />
          {photoArray.length}
        </div>
      </button>
    );
  };

  const handleRadioChange = (fieldName: keyof MeasureSpecificationFormData, value: number) => {
    setFormData(prev => {
        const newExtensions = Array.from({ length: value }, (_, i) => ({
            ...prev.extensions[i] || {
                id: i + 1,
                propertyRoofType: '',
                loftNeedsInsulating: null,
                loftHasExistingInsulation: null,
                depthOfInsulation: '',
                accessToAllAreas: null,
                loftHatchPresent: null,
                loftLadderPresent: null,
                loftHatchInsulated: null,
                loftHatchDraughtProofed: null,
                loftHatchLengthMM: '',
                loftHatchWidthMM: '',
                loftLength: '',
                loftWidth: '',
                loftHeight: '',
                roofPitch: '',
                inaccessibleAreaM2: '',
                walkboardsRequired: null,
                tenantsBelongingsToBeRemoved: null,
                explainedToTenants: null,
                downlightsPresent: null,
                downlightCoversRequired: null,
                coldWaterTankPresent: null,
                pipeworkPresent: null,
                coldWaterTankPipeworkNeedInsulation: null,
                roofVentsPresent: null,
                verticalFlueInLoft: null,
                highCurrentCablesPresent: null,
            }
        }));
        return { ...prev, [fieldName]: value, extensions: newExtensions };
    });
  };

  const handleExtensionChange = (index: number, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
        ...prev,
        extensions: prev.extensions.map((ext, i) => i === index ? { ...ext, [name]: value } : ext)
    }));
  };

  const handleExtensionBoolean = (index: number, fieldName: keyof ExtensionLoftData, value: boolean) => {
    setFormData(prev => ({
        ...prev,
        extensions: prev.extensions.map((ext, i) => i === index ? { ...ext, [fieldName]: value } : ext)
    }));
  };

  // Handler for wall insulation elevation count changes
  const handleElevationCountChange = (value: string) => {
    const count = parseInt(value) || 0;
    
    // Get existing elevations
    const existingElevations = surveyData?.measure_specification?.wallInsulationElevations || [];
    
    // Preserve existing elevations and add/remove as needed
    let elevations;
    if (count > existingElevations.length) {
      // Add new elevations while preserving existing ones
      const newElevations = Array.from({ length: count - existingElevations.length }, (_, i) => ({
        id: existingElevations.length + i + 1, // Use number IDs to match type definition
        type: '', // Will be set by user
      }));
      elevations = [...existingElevations, ...newElevations];
    } else {
      // Keep only the first 'count' elevations
      elevations = existingElevations.slice(0, count);
    }

    // Update form data
    setFormData(prev => ({
      ...prev,
      wallInsulationElevationsCount: value,
    }));

    // Update retrofit survey data
    if (onDataUpdate) {
      onDataUpdate(prev => ({
        ...prev,
        measure_specification: {
          ...prev.measure_specification,
          wallInsulationElevationsCount: value,
          wallInsulationElevations: elevations,
        },
      }), 'measure_specification');
    }
  };

  // Handler for wall insulation elevation type changes
  const handleElevationTypeChange = (elevationId: number, newType: string) => {
    if (!onDataUpdate || !surveyData) return;

    const updatedElevations = (surveyData.measure_specification.wallInsulationElevations || []).map(e =>
      e.id === elevationId ? { ...e, type: newType } : e
    );

    onDataUpdate(prev => ({
      ...prev,
      measure_specification: {
        ...prev.measure_specification,
        wallInsulationElevations: updatedElevations,
      },
    }), 'measure_specification');
  };

  const handlePhotosCaptured = (photoType: keyof MeasureSpecificationPhotos, capturedPhotos: PhotoData[], id?: number) => {
      const displayablePhotos: DisplayablePhotoData[] = capturedPhotos.map((p, i) => {
        const displayId = `${photoType}-${Date.now()}-${i}`;
        const dataUrl = `data:image/${p.format};base64,${p.base64String}`;
        
        // Queue photo for CDN upload
        queuePhotoUpload(
          displayId,
          dataUrl,
          'measure_specification',
          photoType,
          (url) => {
            // Update photo with CDN URL
            setPhotos(prev => {
              if (photoType === 'mainPropertyInsulationDepthEvidence') {
                return {
                  ...prev,
                  [photoType]: (prev[photoType] as DisplayablePhotoData[]).map(photo =>
                    photo.displayId === displayId ? { ...photo, url, uploading: false, fileName: url } : photo
                  )
                };
              } else if (id && ['insulationDepthEvidence', 'loftHatchEvidence', 'roofPitchEvidence', 'loftAreaImages', 'downlightsImage', 'coldWaterTankPipeworkEvidence', 'imagesOfRoofVents'].includes(photoType)) {
                return {
                  ...prev,
                  extensionPhotos: prev.extensionPhotos.map(ext => {
                    const extPhoto = (ext as any)[photoType];
                    return ext.id === id && extPhoto?.displayId === displayId
                      ? { ...ext, [photoType]: { ...extPhoto, url, uploading: false, fileName: url } } as any
                      : ext;
                  })
                };
              } else {
                const currentPhoto = prev[photoType] as DisplayablePhotoData | null;
                return currentPhoto?.displayId === displayId
                  ? { ...prev, [photoType]: { ...currentPhoto, url, uploading: false, fileName: url } }
                  : prev;
              }
            });
          },
          (error) => {
            // Mark upload as failed
            setPhotos(prev => {
              if (photoType === 'mainPropertyInsulationDepthEvidence') {
                return {
                  ...prev,
                  [photoType]: (prev[photoType] as DisplayablePhotoData[]).map(photo =>
                    photo.displayId === displayId ? { ...photo, uploading: false, error } : photo
                  )
                };
              } else if (id && ['insulationDepthEvidence', 'loftHatchEvidence', 'roofPitchEvidence', 'loftAreaImages', 'downlightsImage', 'coldWaterTankPipeworkEvidence', 'imagesOfRoofVents'].includes(photoType)) {
                return {
                  ...prev,
                  extensionPhotos: prev.extensionPhotos.map(ext => {
                    const extPhoto = (ext as any)[photoType];
                    return ext.id === id && extPhoto?.displayId === displayId
                      ? { ...ext, [photoType]: { ...extPhoto, uploading: false, error } } as any
                      : ext;
                  })
                };
              } else {
                const currentPhoto = prev[photoType] as DisplayablePhotoData | null;
                return currentPhoto?.displayId === displayId
                  ? { ...prev, [photoType]: { ...currentPhoto, uploading: false, error } }
                  : prev;
              }
            });
          }
        );
        
        return {
          ...p,
          displayId,
          dataUrl,
          uploading: true,
        };
      });

      if (photoType === 'mainPropertyInsulationDepthEvidence') {
        setPhotos(prev => ({...prev, [photoType]: [...(prev[photoType] as DisplayablePhotoData[]), ...displayablePhotos] }));
      } else if (id && ['insulationDepthEvidence', 'loftHatchEvidence', 'roofPitchEvidence', 'loftAreaImages', 'downlightsImage', 'coldWaterTankPipeworkEvidence', 'imagesOfRoofVents'].includes(photoType)) {
          setPhotos(prev => {
              const newExtPhotos = prev.extensionPhotos.filter(ep => ep.id !== id);
              const existingExt = prev.extensionPhotos.find(ep => ep.id === id) || {
                  id, 
                  insulationDepthEvidence: null, 
                  loftHatchEvidence: null, 
                  roofPitchEvidence: null,
                  loftAreaImages: null,
                  downlightsImage: null,
                  coldWaterTankPipeworkEvidence: null,
                  imagesOfRoofVents: null,
               };
               return {
                   ...prev,
                   extensionPhotos: [...newExtPhotos, { ...existingExt, [photoType]: displayablePhotos[0] || null }]
               }
          });
      }
      else {
        setPhotos(prev => ({ ...prev, [photoType]: displayablePhotos[0] || null }));
      }
  };

  const removePhoto = (photoType: keyof MeasureSpecificationPhotos, displayId?: string, id?: number) => {
    if (photoType === 'mainPropertyInsulationDepthEvidence') {
      setPhotos(prev => ({...prev, [photoType]: (prev[photoType] as DisplayablePhotoData[]).filter(p => p.displayId !== displayId)}));
    } else if (id && ['insulationDepthEvidence', 'loftHatchEvidence', 'roofPitchEvidence', 'loftAreaImages', 'downlightsImage', 'coldWaterTankPipeworkEvidence', 'imagesOfRoofVents'].includes(photoType)) {
        setPhotos(prev => {
              const newExtPhotos = prev.extensionPhotos.filter(ep => ep.id !== id);
              const existingExt = prev.extensionPhotos.find(ep => ep.id === id);
              if (!existingExt) return prev;
              
              return {
                  ...prev,
                  extensionPhotos: [...newExtPhotos, { ...existingExt, [photoType]: null }]
              }
        });
    } else {
      setPhotos(prev => ({ ...prev, [photoType]: null }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Submitting Measure Specification Data:', { formData, photos });
    onNavigateNext();
  };
  
  const rirFaRa = useMemo(() => {
    const floorArea = parseFloat(formData.rirFloorArea) || 0;
    const residualLoft = parseFloat(formData.rirResidualLoftArea) || 0;
    return floorArea + residualLoft;
  }, [formData.rirFloorArea, formData.rirResidualLoftArea]);

  const useTotalLoftArea = (ext: ExtensionLoftData) => useMemo(() => {
        const length = parseFloat(ext.loftLength) || 0;
        const width = parseFloat(ext.loftWidth) || 0;
        return length * width;
    }, [ext.loftLength, ext.loftWidth]);

   const useTotalLoftAreaToBeInsulated = (ext: ExtensionLoftData, totalArea: number) => useMemo(() => {
        const inaccessible = parseFloat(ext.inaccessibleAreaM2) || 0;
        return totalArea - inaccessible;
   }, [ext.inaccessibleAreaM2, totalArea]);

  return (
    <div className="bg-white/80 backdrop-blur-sm p-6 sm:p-8 rounded-xl shadow-lg border border-yellow-200 min-h-[calc(100vh-12rem)] flex flex-col">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 border-b-2 border-gray-200 pb-4 mb-6 flex items-center gap-3">
        <PackagePlus />
        Measure Specification
      </h2>
      <form onSubmit={handleSubmit} className="flex-1 flex flex-col space-y-6 overflow-y-auto pr-2 custom-scrollbar">
        <div className="space-y-4">

          <fieldset className={fieldsetClassName}>
            <legend className={legendClassName}>Measure Selection</legend>
            <div className="grid grid-cols-2 gap-4 mt-2">
              {(['Wall Insulation', 'Loft insulation', 'RIR insulation', 'Heating System', 'No Measures'] as const).map(measure => (
                <label key={measure} className="flex items-center space-x-3">
                  <input type="checkbox" name={measure} checked={formData.suitableMeasures.includes(measure)} onChange={handleCheckboxChange} className="h-5 w-5 rounded border-gray-300 text-yellow-500 focus:ring-yellow-400" />
                  <span className="text-sm font-medium text-gray-700">{measure}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {formData.suitableMeasures.includes('Wall Insulation') && (
            <fieldset className={fieldsetClassName}>
              <legend className={legendClassName}>Wall Insulation Survey</legend>
              
              <div className={fieldWrapperClass}>
                <label htmlFor="wallInsulationType" className={labelClass}>{requiredMarker}Type of Insulation</label>
                <select
                  id="wallInsulationType"
                  name="wallInsulationType"
                  value={formData.wallInsulationType}
                  onChange={handleInputChange}
                  className={`${selectClassName} sm:justify-self-end`}
                  required
                >
                  <option value="cavity">Cavity Wall Insulation</option>
                  <option value="internal-iwi">Internal Wall Insulation (IWI)</option>
                  <option value="external-ewi">External Wall Insulation (EWI)</option>
                </select>
              </div>

              <div className={fieldWrapperClass}>
                <label htmlFor="wallInsulationElevationsCount" className={labelClass}>{requiredMarker}Number of Elevations</label>
                <select
                  id="wallInsulationElevationsCount"
                  name="wallInsulationElevationsCount"
                  value={formData.wallInsulationElevationsCount}
                  onChange={(e) => handleElevationCountChange(e.target.value)}
                  className={`${selectClassName} sm:justify-self-end`}
                  required
                >
                  <option value="">Select number</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                </select>
              </div>

              {/* Dynamic Elevation Sections */}
              {surveyData?.measure_specification.wallInsulationElevations && 
               surveyData.measure_specification.wallInsulationElevations.length > 0 && (
                <div className="mt-6 space-y-4">
                  <h4 className="text-lg font-medium text-gray-800">Elevation Types</h4>
                  {surveyData.measure_specification.wallInsulationElevations.map((elevation, index) => (
                    <div key={elevation.id} className="p-4 bg-gray-50 rounded-lg border">
                      <div className={fieldWrapperClass}>
                        <label htmlFor={`elevation-${elevation.id}`} className={labelClass}>
                          {requiredMarker}Elevation {index + 1}
                        </label>
                        <select
                          id={`elevation-${elevation.id}`}
                          name={`elevation-${elevation.id}`}
                          value={elevation.type}
                          onChange={(e) => handleElevationTypeChange(elevation.id, e.target.value)}
                          className={`${selectClassName} sm:justify-self-end`}
                          required
                        >
                          <option value="">Select elevation</option>
                          <option value="front">Front</option>
                          <option value="rear">Rear</option>
                          <option value="left">Left</option>
                          <option value="right">Right</option>
                        </select>
                      </div>

                      {/* Show diagram when elevation type is selected */}
                      {elevation.type && (
                        <PropertyElevationDiagram 
                          key={`${elevation.type}-${elevation.id}-${restorationCount}`}
                          elevationType={elevation.type}
                          elevationId={elevation.type}
                          elevationNumber={index + 1}
                          propertyData={{
                            propertyType: surveyData.initial_questionnaire.dwellingType?.toLowerCase() || 'house',
                            roofTypeRdSAP: surveyData.property_front?.roofType || 'PA',
                            groundLevel: 'G',
                            detachment: 'detached', // Default to detached, can be enhanced later with actual detachment data
                            floorCount: (() => {
                              // Get floor count from linked floor plan data if available (most current)
                              if (surveyData.floor_plan?.linkedFloorPlanData?.floorPlans) {
                                const floorPlans = surveyData.floor_plan.linkedFloorPlanData.floorPlans;
                                return Object.keys(floorPlans).length || 1;
                              }
                              // Fallback to room_details (may be stale)
                              if (surveyData.room_details?.rooms && surveyData.room_details.rooms.length > 0) {
                                return Math.max(...surveyData.room_details.rooms.map(r => r.floor || 1));
                              }
                              // Default to 2 floors
                              return 2;
                            })()
                          }}
                          wallInsulationType={formData.wallInsulationType}
                          initialPlacedElements={retrofitElevationData[elevation.type]?.placedElements?.filter(el => 
                            ['window', 'door', 'airvent'].includes(el.type)
                          ) as Array<{
                            id: string;
                            type: 'window' | 'door' | 'airvent';
                            x: number;
                            y: number;
                            elevation: string;
                            height?: number;
                            width?: number;
                          }> || []}
                          initialDimensions={retrofitElevationData[elevation.type]?.dimensions}
                          floorPlanWindows={
                            // Extract actual window data from room details
                            surveyData.room_details?.rooms?.flatMap(room => {
                              const roomWindows: Array<{
                                id: string;
                                width: number;
                                height: number;
                                floor: number;
                                orientation: string;
                                roomName: string;
                              }> = [];
                              
                              // Use actual windowDetails from room survey if available
                              const windowDetails = (room as any).windowDetails || [];
                              
                              if (Array.isArray(windowDetails) && windowDetails.length > 0) {
                                // Map each window from the room's windowDetails
                                windowDetails.forEach((window: any, index: number) => {
                                  // Window dimensions are in meters, convert to mm
                                  const widthMm = window.width ? parseFloat(window.width) * 1000 : 1500;
                                  const heightMm = window.height ? parseFloat(window.height) * 1000 : 1000;
                                  
                                  // Generate a unique ID using room.id, floor, and window index
                                  // This prevents collisions when multiple rooms have windows
                                  const uniqueId = `window-f${room.floor}-r${room.id}-w${index + 1}`;
                                  
                                  roomWindows.push({
                                    id: uniqueId,
                                    width: widthMm,
                                    height: heightMm,
                                    floor: room.floor || 1,
                                    orientation: window.orientation || 'Unknown',
                                    roomName: room.name || 'Unknown Room'
                                  });
                                });
                              }
                              
                              return roomWindows;
                            }) || []
                          }
                          walls={[]} // No floor plan walls in retrofit survey context
                          roomCustomLabels={{}}
                          currentRooms={[]}
                          onElevationDataChange={handleRetrofitElevationDataChange}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Global Property-Wide Calculations */}
              {surveyData?.measure_specification.wallInsulationElevations && 
               surveyData.measure_specification.wallInsulationElevations.length > 0 && 
               Object.keys(retrofitElevationData).length > 0 && (
                <div className="mt-8 p-6 bg-blue-50 rounded-lg border-2 border-blue-200">
                  <h4 className="text-xl font-semibold text-blue-900 mb-6 text-center">
                    Property-Wide Wall Insulation Calculations
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-white p-4 rounded-lg border border-blue-200 shadow-sm">
                      <div className="text-sm font-medium text-blue-700 mb-2">Total Dwelling Area</div>
                      <div className="text-2xl font-bold text-blue-900">
                        {retrofitGlobalCalculations.totalDwellingArea.toFixed(2)}m²
                      </div>
                      <div className="text-xs text-blue-600 mt-1">Sum of all elevation areas</div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg border border-blue-200 shadow-sm">
                      <div className="text-sm font-medium text-red-700 mb-2">Total Untreatable Area</div>
                      <div className="text-2xl font-bold text-red-900">
                        {retrofitGlobalCalculations.totalUntreatableArea.toFixed(2)}m²
                      </div>
                      <div className="text-xs text-red-600 mt-1">Windows, doors, vents</div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg border border-blue-200 shadow-sm">
                      <div className="text-sm font-medium text-green-700 mb-2">Total Treatable Area</div>
                      <div className="text-2xl font-bold text-green-900">
                        {retrofitGlobalCalculations.totalTreatableArea.toFixed(2)}m²
                      </div>
                      <div className="text-xs text-green-600 mt-1">Available for insulation</div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg border border-blue-200 shadow-sm">
                      <div className="text-sm font-medium text-purple-700 mb-2">POPT Percentage</div>
                      <div className="text-2xl font-bold text-purple-900">
                        {retrofitGlobalCalculations.poptPercentage.toFixed(1)}%
                      </div>
                      <div className="text-xs text-purple-600 mt-1">Percentage of Premises Treated</div>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-white rounded-lg border border-blue-200">
                    <div className="text-sm font-medium text-gray-700 mb-2">Total Wall Area (Whole House)</div>
                    <div className="text-xl font-bold text-gray-900">
                      {retrofitGlobalCalculations.totalWallAreaWholeHouse.toFixed(2)}m²
                    </div>
                  </div>
                </div>
              )}
            </fieldset>
          )}

          {formData.suitableMeasures.includes('RIR insulation') && (
            <fieldset className={fieldsetClassName}>
              <legend className={legendClassName}>Room in Roof (RIR) Insulation Survey</legend>
              
              <div className="mt-4">
                <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-xs text-blue-700">
                    <strong>📐 Interactive RIR Diagram:</strong> Measure and document room-in-roof areas below.
                  </p>
                </div>

                <RoomInRoofDiagram
                  onDataChange={handleRetrofitRirDataChange}
                  initialMeasurements={retrofitRirData.measurements}
                  initialAnnotations={retrofitRirData.annotations}
                  initialInsulationType={retrofitRirData.insulationType}
                  initialVentilationType={retrofitRirData.ventilationType}
                  initialDiagramType={retrofitRirData.diagramType}
                  initialDiagramImage={retrofitRirData.diagramImage}
                />
              </div>

              {/* RIR Photo Evidence Section */}
              <div className="mt-3 space-y-1.5">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Photo Evidence</h4>
                
                {/* Exterior Elevations */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700 block">Exterior Elevations</label>
                  <PhotoCapture
                    onPhotosCaptured={(p) => handleRIRPhotosCaptured('rirExteriorElevations', p)}
                    maxPhotos={999}
                    buttonLabel="Camera"
                    buttonClass={photoButtonClass}
                  />
                  {renderPhotoPreview(photos.rirExteriorElevations, 'rirExteriorElevations', 'Exterior Elevations')}
                </div>

                {/* Internal Room Photos */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700 block">Internal Room Photos</label>
                  <PhotoCapture
                    onPhotosCaptured={(p) => handleRIRPhotosCaptured('rirInternalRoomPhotos', p)}
                    maxPhotos={999}
                    buttonLabel="Camera"
                    buttonClass={photoButtonClass}
                  />
                  {renderPhotoPreview(photos.rirInternalRoomPhotos, 'rirInternalRoomPhotos', 'Internal Room Photos')}
                </div>

                {/* Flat Ceiling & Insulation */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700 block">Flat Ceiling & Insulation</label>
                  <PhotoCapture
                    onPhotosCaptured={(p) => handleRIRPhotosCaptured('rirFlatCeilingInsulation', p)}
                    maxPhotos={999}
                    buttonLabel="Camera"
                    buttonClass={photoButtonClass}
                  />
                  {renderPhotoPreview(photos.rirFlatCeilingInsulation, 'rirFlatCeilingInsulation', 'Flat Ceiling & Insulation')}
                </div>

                {/* Sloping Ceiling & Insulation */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700 block">Sloping Ceiling & Insulation</label>
                  <PhotoCapture
                    onPhotosCaptured={(p) => handleRIRPhotosCaptured('rirSlopingCeilingInsulation', p)}
                    maxPhotos={999}
                    buttonLabel="Camera"
                    buttonClass={photoButtonClass}
                  />
                  {renderPhotoPreview(photos.rirSlopingCeilingInsulation, 'rirSlopingCeilingInsulation', 'Sloping Ceiling & Insulation')}
                </div>

                {/* Stud Walls & Insulation */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700 block">Stud Walls & Insulation</label>
                  <PhotoCapture
                    onPhotosCaptured={(p) => handleRIRPhotosCaptured('rirStudWallsInsulation', p)}
                    maxPhotos={999}
                    buttonLabel="Camera"
                    buttonClass={photoButtonClass}
                  />
                  {renderPhotoPreview(photos.rirStudWallsInsulation, 'rirStudWallsInsulation', 'Stud Walls & Insulation')}
                </div>

                {/* Dormer Present Boolean */}
                <div className={fieldWrapperClass}>
                  <label className={labelClass}>{requiredMarker}Is there a Dormer present?</label>
                  <div className="flex space-x-4 mt-2">
                    <button 
                      type="button" 
                      onClick={() => handleBooleanButtonClick('dormerPresent', true)} 
                      className={`flex-1 py-3 rounded-lg border-2 ${formData.dormerPresent === true ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors font-medium`}
                    >
                      Yes
                    </button>
                    <button 
                      type="button" 
                      onClick={() => handleBooleanButtonClick('dormerPresent', false)} 
                      className={`flex-1 py-3 rounded-lg border-2 ${formData.dormerPresent === false ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors font-medium`}
                    >
                      No
                    </button>
                  </div>
                </div>

                {/* Dormer Photos (conditional) */}
                {formData.dormerPresent === true && (
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700 block">Dormer Photos</label>
                    <PhotoCapture
                      onPhotosCaptured={(p) => handleRIRPhotosCaptured('rirDormerPhotos', p)}
                      maxPhotos={999}
                      buttonLabel="Camera"
                      buttonClass={photoButtonClass}
                    />
                    {renderPhotoPreview(photos.rirDormerPhotos, 'rirDormerPhotos', 'Dormer Photos')}
                  </div>
                )}
              </div>
            </fieldset>
          )}

          {formData.suitableMeasures.includes('Heating System') && (
           <fieldset className={fieldsetClassName}>
              <legend className={legendClassName}>Heating System Survey</legend>
              <StyledBooleanField label="Is there a Boiler present?" fieldName="isBoilerPresent" value={formData.isBoilerPresent} onUpdate={handleBooleanButtonClick} yesLabel="YES" noLabel="NO"/>
              
              {formData.isBoilerPresent && (
                <>
                  <fieldset className={fieldsetClassName}>
                      <legend className={legendClassName}>Boiler Tech Survey</legend>
                      
                      {/* Universal field - applies to all systems */}
                      <StyledBooleanField label="Will the New Heating System Heat 100% of the Property?" fieldName="newSystemHeat100Percent" value={formData.newSystemHeat100Percent} onUpdate={handleBooleanButtonClick} yesLabel="YES" noLabel="NO"/>
                      <SelectField label="Post Heating System" name="postHeatingSystem" value={formData.postHeatingSystem} options={["Condensing Gas Boiler", "Electric Boiler", "ASHP/Gas Boiler Hybrid", "ASHP", "ESH"]} onChange={handleInputChange} />
                      
                      {/* Flue-related fields - Only for combustion systems (Gas Boiler & Hybrid) */}
                      {(formData.postHeatingSystem === "Condensing Gas Boiler" || formData.postHeatingSystem === "ASHP/Gas Boiler Hybrid") && (
                        <>
                          <SelectField label="Flue Type" name="flueType" value={formData.flueType} options={["Open", "Room-Sealed", "Fan Assisted", "Balanced Flue", "Other"]} onChange={handleInputChange} />
                          <StyledBooleanField label="Flue Guard Required" fieldName="flueGuardRequired" value={formData.flueGuardRequired} onUpdate={handleBooleanButtonClick} yesLabel="YES" noLabel="NO"/>
                          <StyledBooleanField label="Flue Fitting Kit Required" fieldName="flueFittingKitRequired" value={formData.flueFittingKitRequired} onUpdate={handleBooleanButtonClick} yesLabel="YES" noLabel="NO"/>
                          <StyledBooleanField label="Plume Kit Required" fieldName="plumeKitRequired" value={formData.plumeKitRequired} onUpdate={handleBooleanButtonClick} yesLabel="YES" noLabel="NO"/>
                        </>
                      )}
                      
                      {/* Frost Protection - All wet systems + ASHP (not ESH) */}
                      {formData.postHeatingSystem !== "ESH" && (
                        <StyledBooleanField label="Frost Protection Required" fieldName="frostProtectionRequired" value={formData.frostProtectionRequired} onUpdate={handleBooleanButtonClick} yesLabel="YES" noLabel="NO"/>
                      )}
                      
                      {/* Gas/Condensing-specific fields - Only for gas systems */}
                      {(formData.postHeatingSystem === "Condensing Gas Boiler" || formData.postHeatingSystem === "ASHP/Gas Boiler Hybrid") && (
                        <>
                          <SelectField label="Condense Termination" name="condenseTermination" value={formData.condenseTermination} options={["External"]} onChange={handleInputChange} />
                          <InputField label="Distance between Gas meter and Boiler location (pipe run distance)" name="gasMeterBoilerDistance" placeholder="enter number" value={formData.gasMeterBoilerDistance} onChange={handleInputChange} />
                        </>
                      )}
                      
                      {/* Remedial Works - All systems except ESH */}
                      {formData.postHeatingSystem !== "ESH" && (
                        <StyledBooleanField label="Remedial Works Required to Brickwork?" fieldName="remedialWorksToBrickwork" value={formData.remedialWorksToBrickwork} onUpdate={handleBooleanButtonClick} yesLabel="YES" noLabel="NO"/>
                      )}
                      
                      {/* New Boiler/Unit Location - All except ESH */}
                      {formData.postHeatingSystem !== "ESH" && (
                        <SelectField 
                          label={formData.postHeatingSystem === "ASHP" ? "New Heat Pump Unit Location" : "New Boiler Location"} 
                          name="newBoilerLocation" 
                          value={formData.newBoilerLocation} 
                          options={["Kitchen", "Utility Room", "Living Room", "Bathroom", "Loft", "Garage", "Under Stairs", "Hallway", "Bedroom", "Other", "Outside Rear garden"]} 
                          onChange={handleInputChange} 
                        />
                      )}
                      
                      {/* Weather Compensator - Boilers & ASHP (not ESH) */}
                      {formData.postHeatingSystem !== "ESH" && (
                        <StyledBooleanField label="Is a Weather Compensator Needed?" fieldName="weatherCompensatorNeeded" value={formData.weatherCompensatorNeeded} onUpdate={handleBooleanButtonClick} yesLabel="YES" noLabel="NO"/>
                      )}
                      
                      {/* Load Compensation - Boilers & ASHP (not ESH) */}
                      {formData.postHeatingSystem !== "ESH" && (
                        <StyledBooleanField label="Is Load Compensation Needed?" fieldName="loadCompensatorNeeded" value={formData.loadCompensatorNeeded} onUpdate={handleBooleanButtonClick} yesLabel="YES" noLabel="NO"/>
                      )}
                      
                      {/* TRVs & Lock Shields - Wet radiator systems only (not ESH) */}
                      {formData.postHeatingSystem !== "ESH" && (
                        <>
                          <StyledBooleanField label="Are TRVs required?" fieldName="trvsRequired" value={formData.trvsRequired} onUpdate={handleBooleanButtonClick} yesLabel="YES" noLabel="NO"/>
                          <StyledBooleanField label="Are lock shields required?" fieldName="lockShieldsRequired" value={formData.lockShieldsRequired} onUpdate={handleBooleanButtonClick} yesLabel="YES" noLabel="NO"/>
                        </>
                      )}
                  </fieldset>

                   {/* Electrical Safety - All systems (all have electrical components) */}
                   <BooleanField label="Property Earth Bonding Present" fieldName="propertyEarthBondingPresent" value={formData.propertyEarthBondingPresent} onUpdate={handleBooleanButtonClick} />
                   
                   {/* Fuse Spur - All systems (all need electrical supply) */}
                   <BooleanField label="Is There a Fuse Spur Present?" fieldName="fuseSpurPresent" value={formData.fuseSpurPresent} onUpdate={handleBooleanButtonClick} />
                   
                   {/* Pipework - Only wet systems (not ESH) */}
                   {formData.postHeatingSystem !== "ESH" && (
                     <BooleanField label="Pipework Present in any Unheated Areas?" fieldName="pipeworkInUnheatedAreas" value={formData.pipeworkInUnheatedAreas} onUpdate={handleBooleanButtonClick} />
                   )}
                </>
              )}
           </fieldset>
          )}

          {formData.suitableMeasures.includes('Loft insulation') && (
            <fieldset className={fieldsetClassName}>
              <legend className={legendClassName}>Loft Survey</legend>
                <SelectField label="Main Property Roof Type" name="mainPropertyRoofType" value={formData.mainPropertyRoofType} options={['Pitched Roof', 'Flat Roof']} onChange={handleInputChange} />
                <BooleanField label="Does the loft need insulating? (min 270mm required insulation)" fieldName="loftNeedsInsulating" value={formData.loftNeedsInsulating} onUpdate={handleBooleanButtonClick} />
                <BooleanField label="Is there any existing insulation?" fieldName="loftHasExistingInsulation" value={formData.loftHasExistingInsulation} onUpdate={handleBooleanButtonClick} />
                {formData.loftHasExistingInsulation === true && (
                  <InputField label="Depth of insulation (mm)" name="mainLoftDepthOfInsulation" placeholder="enter number" value={formData.mainLoftDepthOfInsulation} onChange={handleInputChange} />
                )}
                <div className={photoCaptureContainerClass}>
                  <label className={labelClass}>Insulation Depth Evidence</label>
                  <PhotoCapture
                    onPhotosCaptured={(p) => handleRIRPhotosCaptured('mainPropertyInsulationDepthEvidence', p)}
                    maxPhotos={999}
                    buttonLabel="Capture Insulation Depth Evidence"
                    buttonClass={photoButtonClass}
                  />
                  {renderPhotoPreview(photos.mainPropertyInsulationDepthEvidence, 'mainPropertyInsulationDepthEvidence', 'Insulation Depth Evidence')}
                </div>
                <BooleanField label="Is there access to all areas of the loft to be Insulated?" fieldName="accessToAllAreasOfLoft" value={formData.accessToAllAreasOfLoft} onUpdate={handleBooleanButtonClick} />
                {formData.accessToAllAreasOfLoft === false && (
                  <InputField label="Areas That Are Inaccessible to Insulate? (m2)" name="inaccessibleAreaM2" placeholder="0.000" value={formData.inaccessibleAreaM2} onChange={handleInputChange} />
                )}
                <BooleanField label="Is The Loft Hatch Already present?" fieldName="loftHatchAlreadyPresent" value={formData.loftHatchAlreadyPresent} onUpdate={handleBooleanButtonClick} />
                <BooleanField label="Is There a Loft Ladder?" fieldName="isThereALoftLadder" value={formData.isThereALoftLadder} onUpdate={handleBooleanButtonClick} />
                <BooleanField label="Is The Loft Hatch Already Insulated?" fieldName="isTheLoftHatchAlreadyInsulated" value={formData.isTheLoftHatchAlreadyInsulated} onUpdate={handleBooleanButtonClick} />
                <BooleanField label="Is The Loft Hatch Draught proofed?" fieldName="isTheLoftHatchDraughtProofed" value={formData.isTheLoftHatchDraughtProofed} onUpdate={handleBooleanButtonClick} />
                <InputField label="Loft hatch Length (mm)" name="loftHatchLengthMM" placeholder="enter number" value={formData.loftHatchLengthMM} onChange={handleInputChange} />
                <InputField label="Loft hatch Width (mm)" name="loftHatchWidthMM" placeholder="enter number" value={formData.loftHatchWidthMM} onChange={handleInputChange} />
                <div className={photoCaptureContainerClass}>
                  <label className={labelClass}>Loft Hatch Evidence</label>
                  <PhotoCapture
                    onPhotosCaptured={(p) => handleRIRPhotosCaptured('mainLoftHatchEvidence', p)}
                    maxPhotos={999}
                    buttonLabel="Capture Loft Hatch Evidence"
                    buttonClass={photoButtonClass}
                  />
                  {renderPhotoPreview(photos.mainLoftHatchEvidence, 'mainLoftHatchEvidence', 'Loft Hatch Evidence')}
                </div>
                <InputField label="Main Property Loft Length (m)" name="mainPropertyLoftLength" placeholder="enter number" value={formData.mainPropertyLoftLength} onChange={handleInputChange} />
                <InputField label="Main Property Loft Width (m)" name="mainPropertyLoftWidth" placeholder="enter number" value={formData.mainPropertyLoftWidth} onChange={handleInputChange} />
                <InputField label="Main Property Loft Height (m)" name="mainPropertyLoftHeight" placeholder="enter number" value={formData.mainPropertyLoftHeight} onChange={handleInputChange} />
                
                {/* Roof Pitch with Mode Selection */}
                <div className={fieldWrapperClass}>
                  <label className={labelClass}>Main Property Roof Pitch</label>
                  <div className="space-y-3 sm:justify-self-end w-full">
                    {/* Mode Selection */}
                    <div className="flex rounded-lg shadow-sm">
                      <button
                        type="button"
                        onClick={() => setRoofPitchMode('manual')}
                        className={`flex-1 px-4 py-2 text-sm font-medium rounded-l-md border ${roofPitchMode === 'manual' ? 'bg-blue-600 text-white border-blue-700 z-10' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                      >
                        Manual Entry
                      </button>
                      <button
                        type="button"
                        onClick={() => setRoofPitchMode('tool')}
                        className={`flex-1 px-4 py-2 text-sm font-medium rounded-r-md border -ml-px ${roofPitchMode === 'tool' ? 'bg-blue-600 text-white border-blue-700 z-10' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                      >
                        <Ruler className="w-4 h-4 inline mr-1" />
                        Measuring Tool
                      </button>
                    </div>
                    
                    {/* Manual Mode: Input + Photo Capture */}
                    {roofPitchMode === 'manual' && (
                      <>
                        <input
                          type="text"
                          name="mainPropertyRoofPitch"
                          placeholder="0°-70°"
                          value={formData.mainPropertyRoofPitch}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                        />
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700">Roof Pitch Evidence</label>
                          <PhotoCapture
                            onPhotosCaptured={(p) => handleRIRPhotosCaptured('mainPropertyRoofPitchEvidence', p)}
                            maxPhotos={999}
                            buttonLabel="Capture Roof Pitch Evidence"
                            buttonClass={photoButtonClass}
                          />
                          {renderPhotoPreview(photos.mainPropertyRoofPitchEvidence, 'mainPropertyRoofPitchEvidence', 'Roof Pitch Evidence')}
                        </div>
                      </>
                    )}
                    
                    {/* Tool Mode: Just Measuring Tool Button */}
                    {roofPitchMode === 'tool' && (
                      <div className="space-y-2">
                        <button
                          type="button"
                          onClick={() => setShowAngleMeasurer(true)}
                          className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-colors flex items-center justify-center space-x-2 shadow-md font-medium"
                        >
                          <Ruler className="w-5 h-5" />
                          <span>Use Measuring Tool</span>
                        </button>
                        {formData.mainPropertyRoofPitch && (
                          <div className="text-center">
                            <span className="text-sm text-gray-600">Measured Angle: </span>
                            <span className="text-lg font-bold text-blue-600">{formData.mainPropertyRoofPitch}</span>
                          </div>
                        )}
                        {photos.mainPropertyRoofPitchEvidence.length > 0 && (
                          <div>
                            <label className="text-sm font-medium text-gray-700 block mb-2">Measurement Evidence</label>
                            {renderPhotoPreview(photos.mainPropertyRoofPitchEvidence, 'mainPropertyRoofPitchEvidence', 'Roof Pitch Evidence')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className={photoCaptureContainerClass}>
                  <label className={labelClass}>Main Loft Area Images</label>
                  <PhotoCapture
                    onPhotosCaptured={(p) => handleRIRPhotosCaptured('mainLoftAreaImages', p)}
                    maxPhotos={999}
                    buttonLabel="Capture Main Loft Area Images"
                    buttonClass={photoButtonClass}
                  />
                  {renderPhotoPreview(photos.mainLoftAreaImages, 'mainLoftAreaImages', 'Main Loft Area Images')}
                </div>
                <div className={photoCaptureContainerClass}>
                  <label className={labelClass}>Apex Measurement (From joist/boarding to peak of the roof)</label>
                  <PhotoCapture
                    onPhotosCaptured={(p) => handleRIRPhotosCaptured('apexMeasurementEvidence', p)}
                    maxPhotos={999}
                    buttonLabel="Capture Apex Measurement Evidence"
                    buttonClass={photoButtonClass}
                  />
                  {renderPhotoPreview(photos.apexMeasurementEvidence, 'apexMeasurementEvidence', 'Apex Measurement Evidence')}
                </div>
                <div className={photoCaptureContainerClass}>
                  <label className={labelClass}>Party Wall</label>
                  <PhotoCapture
                    onPhotosCaptured={(p) => handleRIRPhotosCaptured('partyWallEvidence', p)}
                    maxPhotos={999}
                    buttonLabel="Capture Party Wall Evidence"
                    buttonClass={photoButtonClass}
                  />
                  {renderPhotoPreview(photos.partyWallEvidence, 'partyWallEvidence', 'Party Wall Evidence')}
                </div>
                <BooleanField label="Are any Walkboards Required?" fieldName="walkboardsRequired" value={formData.walkboardsRequired} onUpdate={handleBooleanButtonClick} />
                <BooleanField label="Are the occupier belongings to be removed?" fieldName="tenantsBelongingsToBeRemoved" value={formData.tenantsBelongingsToBeRemoved} onUpdate={handleBooleanButtonClick} />
                {formData.tenantsBelongingsToBeRemoved === true && (
                  <BooleanField label="Has this been explained to the occupier?" fieldName="explainedToOccupier" value={formData.explainedToOccupier} onUpdate={handleBooleanButtonClick} />
                )}
                <BooleanField label="Are There any Downlights Present?" fieldName="loftDownlightsPresent" value={formData.loftDownlightsPresent} onUpdate={handleBooleanButtonClick} />
                {formData.loftDownlightsPresent === true && (
                  <>
                    <BooleanField label="Are downlight covers required?" fieldName="downlightCoversRequired" value={formData.downlightCoversRequired} onUpdate={handleBooleanButtonClick} />
                    <div className={photoCaptureContainerClass}>
                      <label className={labelClass}>Downlight Image</label>
                      <PhotoCapture
                        onPhotosCaptured={(p) => handleRIRPhotosCaptured('downlightImage', p)}
                        maxPhotos={999}
                        buttonLabel="Capture Downlight Image"
                        buttonClass={photoButtonClass}
                      />
                      {renderPhotoPreview(photos.downlightImage, 'downlightImage', 'Downlight Image')}
                    </div>
                  </>
                )}
                <BooleanField label="Cold Water Tank Present?" fieldName="coldWaterTankPresent" value={formData.coldWaterTankPresent} onUpdate={handleBooleanButtonClick} />
                {formData.coldWaterTankPresent === true && (
                  <div className={photoCaptureContainerClass}>
                    <label className={labelClass}>Cold Water Tank, Pipework Evidence</label>
                    <PhotoCapture
                      onPhotosCaptured={(p) => handleRIRPhotosCaptured('coldWaterTankPipeworkEvidence', p)}
                      maxPhotos={999}
                      buttonLabel="Capture Cold Water Tank, Pipework Evidence"
                      buttonClass={photoButtonClass}
                    />
                    {renderPhotoPreview(photos.coldWaterTankPipeworkEvidence, 'coldWaterTankPipeworkEvidence', 'Cold Water Tank, Pipework Evidence')}
                  </div>
                )}
                <BooleanField label="Pipework Present?" fieldName="pipeworkPresent" value={formData.pipeworkPresent} onUpdate={handleBooleanButtonClick} />
                <BooleanField label="Does the Cold Water Tank and/or Pipework need any insulation?" fieldName="coldWaterTankPipeworkNeedInsulation" value={formData.coldWaterTankPipeworkNeedInsulation} onUpdate={handleBooleanButtonClick} />
                <BooleanField label="Are there any roof vents present?" fieldName="roofVentsPresent" value={formData.roofVentsPresent} onUpdate={handleBooleanButtonClick} />
                {formData.roofVentsPresent === true && (
                  <div className={photoCaptureContainerClass}>
                    <label className={labelClass}>Images of Roof Vents</label>
                    <PhotoCapture
                      onPhotosCaptured={(p) => handleRIRPhotosCaptured('roofVentsImage', p)}
                      maxPhotos={999}
                      buttonLabel="Capture Images of Roof Vents"
                      buttonClass={photoButtonClass}
                    />
                    {renderPhotoPreview(photos.roofVentsImage, 'roofVentsImage', 'Images of Roof Vents')}
                  </div>
                )}
                <BooleanField label="Is there a vertical flue in the loft?" fieldName="verticalFlueInLoft" value={formData.verticalFlueInLoft} onUpdate={handleBooleanButtonClick} />
                <BooleanField label="Are there any high current cables present? (i.e Shower/PV/Cooker if bungalow or flat)" fieldName="highCurrentCablesPresent" value={formData.highCurrentCablesPresent} onUpdate={handleBooleanButtonClick} />

                {formData.extensions.map((ext, index) => {
                    const totalArea = useTotalLoftArea(ext);
                    const totalAreaToBeInsulated = useTotalLoftAreaToBeInsulated(ext, totalArea);

                    return (
                    <fieldset key={ext.id} className={fieldsetClassName}>
                        <legend className={legendClassName}>Extension {ext.id}</legend>
                        <div className={fieldWrapperClass}>
                            <label htmlFor={`extension${ext.id}_propertyRoofType`} className={labelClass}>{requiredMarker}Extension {ext.id} Property Roof Type</label>
                            <select id={`extension${ext.id}_propertyRoofType`} name="propertyRoofType" value={ext.propertyRoofType} onChange={(e) => handleExtensionChange(index, e)} className={`${selectClassName} sm:justify-self-end`} required>
                                <option value="">Select List Value(s)</option>
                                <option value="Pitched Roof">Pitched Roof</option>
                                <option value="Flat Roof">Flat Roof</option>
                            </select>
                        </div>
                        <BooleanField label="Does the loft need insulating? (min 270mm required insulation)" fieldName="loftNeedsInsulating" value={ext.loftNeedsInsulating} onUpdate={(f,v) => handleExtensionBoolean(index, f as keyof ExtensionLoftData, v)} />
                        <BooleanField label="Is there any existing insulation?" fieldName="loftHasExistingInsulation" value={ext.loftHasExistingInsulation} onUpdate={(f,v) => handleExtensionBoolean(index, f as keyof ExtensionLoftData, v)} />

                        <div className={fieldWrapperClass}>
                            <label htmlFor={`extension${ext.id}_loftLength`} className={labelClass}>{requiredMarker}Extension {ext.id} Loft Length</label>
                            <input type="number" id={`extension${ext.id}_loftLength`} name="loftLength" value={ext.loftLength} onChange={(e) => handleExtensionChange(index, e)} placeholder="enter number" className={`${numericInputClass} sm:justify-self-end`} required />
                        </div>
                        <div className={fieldWrapperClass}>
                            <label htmlFor={`extension${ext.id}_loftWidth`} className={labelClass}>{requiredMarker}Extension {ext.id} Loft Width</label>
                            <input type="number" id={`extension${ext.id}_loftWidth`} name="loftWidth" value={ext.loftWidth} onChange={(e) => handleExtensionChange(index, e)} placeholder="enter number" className={`${numericInputClass} sm:justify-self-end`} required />
                        </div>
                        <div className={fieldWrapperClass}>
                            <label htmlFor={`extension${ext.id}_loftHeight`} className={labelClass}>{requiredMarker}Extension {ext.id} Loft Height</label>
                            <input type="number" id={`extension${ext.id}_loftHeight`} name="loftHeight" value={ext.loftHeight} onChange={(e) => handleExtensionChange(index, e)} placeholder="enter number" className={`${numericInputClass} sm:justify-self-end`} required />
                        </div>
                        <div className={fieldWrapperClass}>
                            <label htmlFor={`extension${ext.id}_roofPitch`} className={labelClass}>{requiredMarker}Ext {ext.id} - Roof Pitch</label>
                            <input type="number" id={`extension${ext.id}_roofPitch`} name="roofPitch" value={ext.roofPitch} onChange={(e) => handleExtensionChange(index, e)} placeholder="0°-70°" className={`${numericInputClass} sm:justify-self-end`} required />
                        </div>
                        <PhotoField label={`Ext ${ext.id} - Roof Pitch Evidence`} photoType="roofPitchEvidence" altText={`Extension ${ext.id} Roof Pitch Evidence`} photoData={photos.extensionPhotos.find(p => p.id === ext.id)?.roofPitchEvidence || null} onPhotosCaptured={handlePhotosCaptured} removePhoto={removePhoto} id={ext.id} />
                        <PhotoField label={`Extension ${ext.id} Loft Area Images`} photoType="loftAreaImages" altText={`Extension ${ext.id} Loft Area Images`} photoData={photos.extensionPhotos.find(p => p.id === ext.id)?.loftAreaImages || null} onPhotosCaptured={handlePhotosCaptured} removePhoto={removePhoto} id={ext.id} />
                        <div className={fieldWrapperClass}>
                            <label htmlFor={`extension${ext.id}_inaccessibleAreaM2`} className={labelClass}>{requiredMarker}Areas That Are Inaccessible to Insulate? (m2)</label>
                            <input type="number" id={`extension${ext.id}_inaccessibleAreaM2`} name="inaccessibleAreaM2" value={ext.inaccessibleAreaM2} onChange={(e) => handleExtensionChange(index, e)} placeholder="0.000" className={`${numericInputClass} sm:justify-self-end`} required />
                        </div>
                        <div className={fieldWrapperClass}>
                            <label className={labelClass}>Total Loft Area</label>
                            <span className="font-bold text-gray-800 sm:justify-self-end">{totalArea}</span>
                        </div>
                        <div className={fieldWrapperClass}>
                            <label className={labelClass}>Total Loft Area to be Insulated</label>
                            <span className="font-bold text-gray-800 sm:justify-self-end">{totalAreaToBeInsulated}</span>
                        </div>
                        <BooleanField label="Are any Walkboards Required?" fieldName="walkboardsRequired" value={ext.walkboardsRequired} onUpdate={(f,v) => handleExtensionBoolean(index, f as keyof ExtensionLoftData, v)} />
                        <BooleanField label="Are the occupier belongings to be removed?" fieldName="tenantsBelongingsToBeRemoved" value={ext.tenantsBelongingsToBeRemoved} onUpdate={(f,v) => handleExtensionBoolean(index, f as keyof ExtensionLoftData, v)} />
                        <BooleanField label="Has this been explained to the tenants?" fieldName="explainedToTenants" value={ext.explainedToTenants} onUpdate={(f,v) => handleExtensionBoolean(index, f as keyof ExtensionLoftData, v)} />
                        <BooleanField label="Are There any Downlights Present?" fieldName="downlightsPresent" value={ext.downlightsPresent} onUpdate={(f,v) => handleExtensionBoolean(index, f as keyof ExtensionLoftData, v)} />
                        <BooleanField label="Are downlight covers required?" fieldName="downlightCoversRequired" value={ext.downlightCoversRequired} onUpdate={(f,v) => handleExtensionBoolean(index, f as keyof ExtensionLoftData, v)} />
                        <PhotoField label="Downlights Image" photoType="downlightsImage" altText="Downlights Image" photoData={photos.extensionPhotos.find(p => p.id === ext.id)?.downlightsImage || null} onPhotosCaptured={handlePhotosCaptured} removePhoto={removePhoto} id={ext.id} />
                        <BooleanField label="Cold Water Tank Present?" fieldName="coldWaterTankPresent" value={ext.coldWaterTankPresent} onUpdate={(f,v) => handleExtensionBoolean(index, f as keyof ExtensionLoftData, v)} />
                        <BooleanField label="Pipework Present?" fieldName="pipeworkPresent" value={ext.pipeworkPresent} onUpdate={(f,v) => handleExtensionBoolean(index, f as keyof ExtensionLoftData, v)} />
                        <BooleanField label="Does the Cold Water Tank and/or Pipework need any insulation?" fieldName="coldWaterTankPipeworkNeedInsulation" value={ext.coldWaterTankPipeworkNeedInsulation} onUpdate={(f,v) => handleExtensionBoolean(index, f as keyof ExtensionLoftData, v)} />
                        <PhotoField label="Cold Water tank, Pipework Evidence" photoType="coldWaterTankPipeworkEvidence" altText="Cold Water tank, Pipework Evidence" photoData={photos.extensionPhotos.find(p => p.id === ext.id)?.coldWaterTankPipeworkEvidence || null} onPhotosCaptured={handlePhotosCaptured} removePhoto={removePhoto} id={ext.id} />
                        <BooleanField label="Are there any roof vents present?" fieldName="roofVentsPresent" value={ext.roofVentsPresent} onUpdate={(f,v) => handleExtensionBoolean(index, f as keyof ExtensionLoftData, v)} />
                        <PhotoField label="Images of roof vents" photoType="imagesOfRoofVents" altText="Images of roof vents" photoData={photos.extensionPhotos.find(p => p.id === ext.id)?.imagesOfRoofVents || null} onPhotosCaptured={handlePhotosCaptured} removePhoto={removePhoto} id={ext.id} />
                        <BooleanField label="Is there a vertical flue in the loft?" fieldName="verticalFlueInLoft" value={ext.verticalFlueInLoft} onUpdate={(f,v) => handleExtensionBoolean(index, f as keyof ExtensionLoftData, v)} />
                        <BooleanField label="Are there any high current cables present? (i.e Shower/PV/Cooker if bungalow or flat)" fieldName="highCurrentCablesPresent" value={ext.highCurrentCablesPresent} onUpdate={(f,v) => handleExtensionBoolean(index, f as keyof ExtensionLoftData, v)} />
                    </fieldset>
                )})}
            </fieldset>
          )}


        </div>

        <div className="mt-auto pt-6 border-t border-gray-200 flex justify-between">
          <button type="button" onClick={onNavigateBack} className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02]">
            <ArrowLeft size={18} /> Previous
          </button>
          <button type="submit" className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-400 to-amber-500 text-white font-semibold rounded-lg shadow-md hover:from-yellow-500 hover:to-amber-600 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02]">
            Next <ArrowRight size={18} />
          </button>
        </div>
      </form>

      {/* Photo Gallery Modal */}
      {selectedGallery && (() => {
        const photoData = photos[selectedGallery as keyof typeof photos];
        const photoArray = Array.isArray(photoData) ? photoData : [];
        
        return (
          <PhotoGalleryModal
            isOpen={!!selectedGallery}
            onClose={handleCloseGallery}
            photos={photoArray.map((photo: any) => ({
              id: photo.displayId || photo.id,
              url: photo.url || photo.dataUrl,
              localPath: photo.url || photo.dataUrl,
            }))}
            sectionTitle={selectedGallery.replace(/([A-Z])/g, ' $1').trim()}
            onDeletePhoto={(photoId) => removeRIRPhoto(selectedGallery as keyof MeasureSpecificationPhotos, photoId)}
            onUpdateNote={() => {}}
            onGalleryStateChange={() => {}}
          />
        );
      })()}

      {/* Angle Measurer Modal */}
      {showAngleMeasurer && (
        <RoofPitchAngleMeasurer
          onAngleCaptured={(angle, screenshotDataUrl) => {
            console.log('[Form] Angle captured:', angle, 'Screenshot:', !!screenshotDataUrl);
            
            // Set the angle value
            setFormData(prev => ({ ...prev, mainPropertyRoofPitch: angle }));
            
            // Add screenshot to pitch evidence if available
            if (screenshotDataUrl) {
              const timestamp = Date.now();
              const screenshotPhoto: DisplayablePhotoData = {
                displayId: `measurement-${timestamp}`,
                dataUrl: screenshotDataUrl,
                base64String: screenshotDataUrl,
                fileName: `roof-pitch-measurement-${timestamp}.png`,
                format: 'png'
              };
              
              setPhotos(prev => ({
                ...prev,
                mainPropertyRoofPitchEvidence: [...prev.mainPropertyRoofPitchEvidence, screenshotPhoto]
              }));
              
              console.log('[Form] Screenshot added to pitch evidence');
            }
            
            setShowAngleMeasurer(false);
          }}
          onClose={() => setShowAngleMeasurer(false)}
        />
      )}
    </div>
  );
};

export default MeasureSpecificationForm; 